import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Wallet } from 'ethers';
import { signPredictAccountMessage } from './predictAuth';
import PrimaryArbitragePage from './PrimaryArbitragePage';
import LpArbPage from './LpArbPage';
import GlobalInfoPage from './GlobalInfoPage';
import ConvertibleBondPage from './ConvertibleBondPage';
import VolumeSpikePage from './VolumeSpikePage';
import WanwuMemePage from './WanwuMemePage';
import TapeOutNandPage from './TapeOutNandPage';
import MarsMiningPage from './MarsMiningPage';
import PolyOptionsPage from './PolyOptionsPage';

// 部署到 Cloudflare Pages 時一律用 Worker，不再使用本地連接
// 本地開發時留空 VITE_API_ORIGIN 則走 Vite proxy → localhost:4000
const envOrigin = (import.meta.env.VITE_API_ORIGIN || '').trim().replace(/\/$/, '');
const isPages = typeof window !== 'undefined' && /\.pages\.dev$/i.test(window.location?.hostname || '');
const isPredict2Site = import.meta.env.VITE_SITE_ID === 'predict2' || (typeof window !== 'undefined' && /predict2-web\.pages\.dev$/i.test(window.location?.hostname || ''));
const API_BASE = isPages ? '' : (envOrigin || '');
const api = (path) => (API_BASE ? API_BASE + (path.startsWith('/') ? path : '/' + path) : path);

/** @deprecated 遷移用：舊版按訂單 id 存做市勾選 */
const STORAGE_MM_ORDER_IDS = 'predict_mm_order_ids';
const STORAGE_MM_MARKET_IDS = 'predict_mm_market_ids';
/** 每市场「5 分钟定时重挂」开关：仅 localStorage；计时从勾选启用起满 5 分钟后首次执行 */
const STORAGE_AUTORELIST5_MARKET_IDS = 'predict_autorelist5_market_ids';
/** 各市场下次重挂的时间戳（epoch ms），仅在开关为开时有效 */
const STORAGE_AUTORELIST5_NEXT_AT = 'predict_autorelist5_next_at';
const AUTORELIST_5MIN_MS = 5 * 60 * 1000;
/** 轮询间隔：检查是否已到「勾选起」满 5 分钟的时刻 */
const AUTORELIST_TICK_MS = 30 * 1000;
function loadAutoRelistNextAtFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_AUTORELIST5_NEXT_AT);
    const o = raw ? JSON.parse(raw) : {};
    if (!o || typeof o !== 'object' || Array.isArray(o)) return {};
    const out = {};
    for (const [k, v] of Object.entries(o)) {
      const n = Number(v);
      if (Number.isFinite(n)) out[String(k)] = n;
    }
    return out;
  } catch {
    return {};
  }
}

function parseOrderAmountToBigIntWei(val) {
  if (val == null) return 0n;
  let s = String(val).trim();
  if (!s) return 0n;
  const m = s.match(/^([+-]?\d*\.?\d+)[eE]([+-]?\d+)$/);
  if (m) {
    let mant = m[1];
    const exp = parseInt(m[2], 10);
    let sign = mant.startsWith('-') ? '-' : '';
    if (mant.startsWith('+') || mant.startsWith('-')) mant = mant.slice(1);
    const parts = mant.split('.');
    const digits = parts.join('');
    const decPlaces = parts[1] ? parts[1].length : 0;
    const zeros = exp - decPlaces;
    if (zeros < 0) return 0n;
    s = sign + digits + '0'.repeat(zeros);
  } else if (s.includes('.')) {
    s = s.split('.')[0];
  }
  if (!/^[-]?\d+$/.test(s)) return 0n;
  return BigInt(s);
}

function humanDecimalSharesToWei(s) {
  const t = String(s).trim();
  if (!t || !/^\d+(\.\d+)?$/.test(t)) return null;
  const [w, f = ''] = t.split('.');
  const frac = (f + '0'.repeat(18)).slice(0, 18);
  try {
    return BigInt(w || '0') * 10n ** 18n + BigInt(frac || '0');
  } catch (_) {
    return null;
  }
}

/** 挂单 amount：同 Cloudflare cron / humanAmount，区分 wei 与人类股数 */
function orderAmountToQuantityWei(raw) {
  if (raw == null) return 0n;
  const s0 = String(raw).trim().replace(/,/g, '');
  if (!s0) return 0n;
  if (/[eE]/.test(s0)) {
    return parseOrderAmountToBigIntWei(raw);
  }
  if (s0.includes('.')) {
    const hm = humanDecimalSharesToWei(s0);
    return hm != null && hm > 0n ? hm : 0n;
  }
  if (/^-?\d+$/.test(s0)) {
    try {
      const bi = BigInt(s0);
      if (bi >= 10n ** 16n) return bi;
      return bi * 10n ** 18n;
    } catch (_) {}
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0n;
  if (n >= 1e10) {
    try {
      return BigInt(Math.floor(n));
    } catch (_) {
      return parseOrderAmountToBigIntWei(s0);
    }
  }
  return BigInt(Math.floor(n)) * 10n ** 18n;
}

/** 從限價單 maker/taker（wei 字串）推導每股價格 wei；不可轉 Number，否則大整數丟精度。 */
function pricePerShareWeiFromOrderAmounts(order, isBuy) {
  const makerB = parseOrderAmountToBigIntWei(order?.makerAmount);
  const takerB = parseOrderAmountToBigIntWei(order?.takerAmount);
  if (makerB <= 0n || takerB <= 0n) return null;
  if (isBuy) {
    const p = (makerB * 10n ** 18n) / takerB;
    return p > 0n && p < 10n ** 18n ? p : null;
  }
  const p = (takerB * 10n ** 18n) / makerB;
  return p > 0n && p < 10n ** 18n ? p : null;
}

/** 將 Auth 回傳的 JWT 統一成可安全用於 Bearer 的字串；物件等非純量視為無效 */
function jwtToString(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'bigint' || typeof v === 'boolean') return String(v).trim();
  return '';
}

/** 与 OpenOrdersList 一致：从 markets 上取该 token 的 Predict 买一卖一 */
function getPredictBestBidAskForToken(market, tokenId) {
  if (!market || tokenId == null || tokenId === '') return { bestBid: null, bestAsk: null };
  const tid = String(tokenId);
  const t = market.bestPricesByToken?.[tid] ?? market.bestPricesByToken?.[tokenId];
  if (t && (t.bestBid != null || t.bestAsk != null)) {
    return { bestBid: t.bestBid ?? null, bestAsk: t.bestAsk ?? null };
  }
  const obByToken = market.orderbookByToken || {};
  const predictOb = obByToken[tid]
    ? obByToken[tid]
    : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
  const pBids = predictOb.bidsTop3 || [];
  const pAsks = predictOb.asksTop3 || [];
  return {
    bestBid: pBids[0] != null ? Number(pBids[0][0]) : null,
    bestAsk: pAsks[0] != null ? Number(pAsks[0][0]) : null,
  };
}

const PASSIVE_LIMIT_EPS = 1e-10;

/**
 * 阻挡会立即成交的限价单（买价 ≥ 卖一 / 卖价 ≤ 买一），视同市价吃单风险
 */
function assertPassiveLimitOrThrow(placeFields, market) {
  if (!market || typeof market !== 'object') return;
  const side = Number(placeFields.side);
  const pw = placeFields.pricePerShareWei;
  if (pw == null || pw === '') return;
  let limitDec;
  try {
    limitDec = Number(BigInt(String(pw))) / 1e18;
  } catch (_) {
    return;
  }
  if (!Number.isFinite(limitDec) || limitDec <= 0 || limitDec >= 1) return;
  const { bestBid, bestAsk } = getPredictBestBidAskForToken(market, placeFields.tokenId);
  if (side === 0) {
    if (bestAsk != null && Number.isFinite(bestAsk) && limitDec >= bestAsk - PASSIVE_LIMIT_EPS) {
      throw new Error(
        `买单限价 ${(limitDec * 100).toFixed(2)}¢ 不低于卖一 ${(bestAsk * 100).toFixed(2)}¢，将立即吃单（市价风险），已拒绝提交`
      );
    }
  } else if (side === 1) {
    if (bestBid != null && Number.isFinite(bestBid) && limitDec <= bestBid + PASSIVE_LIMIT_EPS) {
      throw new Error(
        `卖单限价 ${(limitDec * 100).toFixed(2)}¢ 不高于买一 ${(bestBid * 100).toFixed(2)}¢，将立即吃单（市价风险），已拒绝提交`
      );
    }
  }
}

function orderItemToLimitParams(item, market, priceDecimalOverride) {
  const order = item.order || {};
  const marketId = item.marketId;
  const tokenId = order.tokenId;
  const side = order.side;
  if (marketId == null || tokenId == null || side == null) return null;
  const isBuy = Number(side) === 0;
  let pricePerShareWeiStr;
  if (
    priceDecimalOverride != null &&
    Number.isFinite(priceDecimalOverride) &&
    priceDecimalOverride > 0 &&
    priceDecimalOverride < 1
  ) {
    pricePerShareWeiStr = String(BigInt(Math.floor(priceDecimalOverride * 1e18)));
  } else {
    const p = pricePerShareWeiFromOrderAmounts(order, isBuy);
    if (p == null) return null;
    pricePerShareWeiStr = String(p);
  }
  const sharesWei = orderAmountToQuantityWei(item.amount);
  if (sharesWei < BigInt(1e16)) return null;
  const m = market || {};
  return {
    tokenId: String(tokenId),
    side: Number(side),
    sharesWei: sharesWei.toString(),
    pricePerShareWei: pricePerShareWeiStr,
    feeRateBps: Math.max(PREDICT_MIN_FEE_RATE_BPS, Math.floor(Number(m.feeRateBps) || 0)),
    isNegRisk: !!m.isNegRisk,
    isYieldBearing: !!m.isYieldBearing,
  };
}

/** 後端若暫時回傳空 {}，勿覆蓋本地已有資料（JS 中空物件仍為 truthy，舊邏輯會誤清空） */
function mergePolyServerPayload(prev, incoming) {
  if (!incoming || typeof incoming !== 'object') return prev;
  if (Object.keys(incoming).length === 0 && Object.keys(prev).length > 0) return prev;
  return incoming;
}

const STORAGE_API_KEY = 'predict_api_key';
const STORAGE_ADDRESS = 'predict_address';
const STORAGE_POLL = 'predict_poll_interval';
const STORAGE_POLY_POLL = 'predict_poly_poll_interval';
const STORAGE_ACCOUNTS = 'predict_accounts';

const POLL_OPTIONS = [
  { value: 0, label: '不輪詢' },
  { value: 3, label: '3 秒' },
  { value: 5, label: '5 秒' },
  { value: 10, label: '10 秒' },
  { value: 30, label: '30 秒' },
  { value: 60, label: '60 秒' },
  { value: 120, label: '120 秒' },
];

const POLY_POLL_OPTIONS = [
  { value: 1, label: '1 秒' },
  { value: 3, label: '3 秒' },
  { value: 5, label: '5 秒' },
  { value: 10, label: '10 秒' },
  { value: 15, label: '15 秒' },
  { value: 30, label: '30 秒' },
];

/** 與 Predict /orders 校驗一致：簽名時 feeRateBps 不得低於此（市場未返回時須抬到該值） */
const PREDICT_MIN_FEE_RATE_BPS = 180;

// 測試機器人按鈕
function TestBotButton() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const handleTest = async () => {
    setLoading(true);
    setResult(null);
    try {
      const resp = await fetch(api('/api/bot/test'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.success) {
        const parts = [];
      if (data.telegram === true) parts.push('Telegram 已送达');
      if (data.telegram === false) parts.push('Telegram 发送失败');
      if (data.wecom === true) parts.push('企业微信已送达');
      if (data.wecom === false) parts.push('企业微信发送失败');
      if (data.dingtalk === true) parts.push('钉钉已送达');
      if (data.dingtalk === false) parts.push('钉钉发送失败');
        setResult({ ok: true, msg: (parts.length ? parts.join('；') : '') || data.message });
      } else {
        setResult({ ok: false, msg: data?.message || '發送失敗' });
      }
    } catch (e) {
      setResult({ ok: false, msg: e?.message || '網路錯誤' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button type="button" className="positions-query-btn" style={{ background: '#334155' }} onClick={handleTest} disabled={loading}>
        {loading ? '發送中…' : '測試機器人'}
      </button>
      {result && (
        <span style={{ fontSize: 13, color: result.ok ? '#22c55e' : '#ef4444' }} title={result.msg}>
          {result.ok ? '✓ ' + result.msg : '✗ ' + result.msg}
        </span>
      )}
    </div>
  );
}

// 生成唯一 ID
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// 多帳戶管理組件
function AccountManager({ accounts, currentAddress, onSelect, onAdd, onUpdate, onDelete }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newPrivateKey, setNewPrivateKey] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editPrivateKey, setEditPrivateKey] = useState('');

  const handleAdd = () => {
    const addr = newAddress.trim();
    if (!addr) return;
    onAdd({
      address: addr,
      note: newNote.trim(),
      privateKey: newPrivateKey.trim() || undefined,
      telegramEnabled: true,
    });
    setNewAddress('');
    setNewNote('');
    setNewPrivateKey('');
    setShowAddForm(false);
  };

  const handleStartEdit = (acc) => {
    setEditingId(acc.id);
    setEditNote(acc.note || '');
    setEditPrivateKey('');
  };

  const handleSaveEdit = (acc) => {
    const updates = { note: editNote.trim() };
    if (editPrivateKey.trim()) updates.privateKey = editPrivateKey.trim();
    onUpdate(acc.id, updates);
    setEditingId(null);
    setEditNote('');
    setEditPrivateKey('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNote('');
    setEditPrivateKey('');
  };

  return (
    <div className="account-manager">
      <div className="account-manager-header">
        <span className="account-manager-title">已保存帳戶</span>
        <button
          type="button"
          className="account-add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '取消' : '+ 添加帳戶'}
        </button>
      </div>

      {showAddForm && (
        <div className="account-add-form">
          <input
            type="text"
            className="account-input"
            placeholder="存款帳戶地址 0x..."
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
          />
          <input
            type="text"
            className="account-input account-input-note"
            placeholder="備註（可選）"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <input
            type="password"
            className="account-input account-input-key"
            placeholder="Privy 私鑰（可選，用於掛單 JWT）"
            value={newPrivateKey}
            onChange={(e) => setNewPrivateKey(e.target.value)}
          />
          <button
            type="button"
            className="account-save-btn"
            onClick={handleAdd}
            disabled={!newAddress.trim()}
          >
            保存
          </button>
        </div>
      )}

      <div className="account-list">
        {accounts.length === 0 ? (
          <div className="account-list-empty">尚無保存的帳戶</div>
        ) : (
          accounts.map((acc) => {
            const isActive = acc.address.toLowerCase() === currentAddress?.toLowerCase();
            const isEditing = editingId === acc.id;
            return (
              <div
                key={acc.id}
                className={`account-item ${isActive ? 'account-item-active' : ''}`}
              >
                <div className="account-item-main" onClick={() => onSelect(acc)}>
                  <div className="account-item-address" title={acc.address}>
                    {acc.address.slice(0, 6)}…{acc.address.slice(-4)}
                  </div>
                  {isEditing ? (
                    <div className="account-edit-fields" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        className="account-edit-input"
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder="備註"
                      />
                      <input
                        type="password"
                        className="account-edit-input account-edit-key"
                        value={editPrivateKey}
                        onChange={(e) => setEditPrivateKey(e.target.value)}
                        placeholder="留空不變，填寫則更新 Privy 私鑰"
                      />
                    </div>
                  ) : (
                    <div className="account-item-meta">
                      <span className="account-item-note">{acc.note || '—'}</span>
                      {acc.privateKey && (
                        <span className="account-item-key-badge" title="已設定掛單 JWT 密鑰">
                          🔑
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="account-item-actions">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleSaveEdit(acc); }}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                  <label
                    className="account-alert-toggle"
                    title="是否啟用 Telegram 機器人提醒"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={acc.telegramEnabled !== false}
                      onChange={(e) => {
                        onUpdate(acc.id, { telegramEnabled: e.target.checked });
                      }}
                    />
                    <span>提醒</span>
                  </label>
                      <button
                        type="button"
                        className="account-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(acc); }}
                        title="編輯備註"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="account-action-btn account-action-delete"
                        onClick={(e) => { e.stopPropagation(); onDelete(acc.id); }}
                        title="刪除帳戶"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function getOutcomeName(tokenId, outcomes) {
  if (!Array.isArray(outcomes)) return '—';
  const o = outcomes.find((x) => String(x?.onChainId) === String(tokenId));
  return o?.name ?? '—';
}

/** 依市場 + outcome 取 Poly 盤口（多選項市場為 marketId::OutcomeName） */
function getPolyOrderbookForOutcome(polyOrderbookByMarket, marketId, outcomeNameTrimmed) {
  const o = (outcomeNameTrimmed || '').trim();
  return (o && polyOrderbookByMarket?.[`${marketId}::${o}`]) || polyOrderbookByMarket?.[marketId];
}

/**
 * 二元 Yes/No 市場雙邊掛單時，每邊應固定對應 Yes/No 盤口，不受 polyViewMode 強制切換影響。
 * 非 Yes/No 的多選項 outcome 維持原先用 polyViewMode／名稱推斷的邏輯。
 */
function getPolyViewNoForOutcome(outcomeName, polyViewModeByMarketId, marketId) {
  const explicit = (outcomeName || '').trim().toLowerCase();
  if (explicit === 'yes') return false;
  if (explicit === 'no') return true;
  const viewOverride = polyViewModeByMarketId?.[marketId];
  return viewOverride === 'no'
    ? true
    : viewOverride === 'yes'
      ? false
      : explicit === 'no';
}

// 從 Polymarket 體育連結解析兩選項（如 nba-tor-mil-2026-02-22 -> [TOR, MIL]），用於強制切換按鈕
function parsePolyMarketOptions(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(/nba-([a-z]{2,5})-([a-z]{2,5})(?:-\d|$)/i);
  if (m) return [m[1].toUpperCase(), m[2].toUpperCase()];
  const m2 = url.trim().match(/(?:sports\/)?(?:nhl|nfl|epl|mlb)-([a-z]{2,5})-([a-z]{2,5})(?:-\d|$)/i);
  if (m2) return [m2[1].toUpperCase(), m2[2].toUpperCase()];
  return null;
}

/** 從 Polymarket 體育連結提取開賽日期，回傳 YYYY-MM-DDTHH:mm 格式，預設 20:00；無則回傳 null */
function parseStartTimeFromPolyUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.trim().match(/(\d{4})-(\d{2})-(\d{2})(?:\?|$|\/)/);
  if (!m) return null;
  const [, y, mon, d] = m;
  return `${y}-${mon}-${d}T20:00`;
}

// 單一市場下方的 Polymarket 盤口：有 polyOrderbookItem 時顯示卖3→卖1、买1→买3（賣盤由上到下遞減，卖一=最佳賣價緊貼下方；買盤由上到下遞減，买一=最佳買價緊貼上方）；否則顯示買一賣一
function PolymarketDepthInline({ polyData, polyOrderbookItem, loading, orderList, market }) {
  // Hooks 必須在頂層呼叫，不可放在條件或 return 之後
  const orderSideOutcomes = useMemo(() => {
    const set = new Set();
    (orderList || []).forEach((item) => {
      const order = item.order || {};
      const side = order.side === 0 ? 'Buy' : 'Sell';
      const outcomeName = getOutcomeName(order.tokenId, market?.outcomes);
      const outcome = String(outcomeName).toLowerCase() === 'yes' ? 'Yes' : 'No';
      set.add(`${side}_${outcome}`);
    });
    return [...set];
  }, [orderList, market?.outcomes]);

  // 若已有完整 orderbook，就直接顯示，不因全局 loading 反覆切換「載入中」，避免畫面跳動
  const polyAsksAsc = (polyOrderbookItem?.asks || []).slice().sort((a, b) => (a[0] || 0) - (b[0] || 0)).slice(0, 3);
  const polyAsks = polyAsksAsc.slice().reverse();
  const polyBids = (polyOrderbookItem?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0)).slice(0, 3);
  const hasFullBook = polyAsks.length > 0 || polyBids.length > 0;
  if (loading && !hasFullBook) {
    return (
      <div className="polymarket-inline polymarket-inline-loading">
        Polymarket 買一賣一：載入中…
      </div>
    );
  }
  // Poly 賣單：表格由上到下價格遞減，卖一=最低賣價（最貼近下方買盤）
  if (hasFullBook) {
    return (
      <div className="polymarket-inline">
        <div className="polymarket-inline-title">Poly 市場（依掛單 outcome）</div>
        <div className="polymarket-inline-orderbook">
          <table className="orders-table polymarket-inline-table">
            <thead>
              <tr><th>賣</th><th>價格</th><th>數量</th><th>總計</th></tr>
            </thead>
            <tbody>
              {polyAsks.map(([p, s], i) => (
                <tr key={`a${i}`} className="boost-ask-row">
                  <td>賣{polyAsks.length - i}</td>
                  <td>{(p * 100).toFixed(0)}¢</td>
                  <td>{s.toFixed(0)}</td>
                  <td>${(p * s).toFixed(2)}</td>
                </tr>
              ))}
              {polyAsks.length === 0 && <tr><td colSpan={4} className="boost-empty">—</td></tr>}
            </tbody>
          </table>
          <table className="orders-table polymarket-inline-table">
            <thead>
              <tr><th>買</th><th>價格</th><th>數量</th><th>總計</th></tr>
            </thead>
            <tbody>
              {polyBids.map(([p, s], i) => (
                <tr key={`b${i}`} className="boost-bid-row">
                  <td>買{i + 1}</td>
                  <td>{(p * 100).toFixed(0)}¢</td>
                  <td>{s.toFixed(0)}</td>
                  <td>${(p * s).toFixed(2)}</td>
                </tr>
              ))}
              {polyBids.length === 0 && <tr><td colSpan={4} className="boost-empty">—</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (!polyData) return null;

  const rowConfig = [
    { key: 'Buy_Yes', side: 'Buy', outcome: 'Yes', label: '买一', data: polyData.buyYes },
    { key: 'Buy_No', side: 'Buy', outcome: 'No', label: '买一', data: polyData.buyNo },
    { key: 'Sell_Yes', side: 'Sell', outcome: 'Yes', label: '卖一', data: polyData.sellYes },
    { key: 'Sell_No', side: 'Sell', outcome: 'No', label: '卖一', data: polyData.sellNo },
  ];
  const rowsToShow = rowConfig.filter((r) => orderSideOutcomes.includes(r.key) && r.data != null);
  if (rowsToShow.length === 0) return null;
  return (
    <div className="polymarket-inline">
      <div className="polymarket-inline-title">Polymarket 買一賣一</div>
      <table className="orders-table polymarket-inline-table">
        <thead>
          <tr>
            <th>Side</th>
            <th>Outcome</th>
            <th />
            <th>Price</th>
            <th>Shares</th>
          </tr>
        </thead>
        <tbody>
          {rowsToShow.map((row) => {
            const d = row.data;
            const isYes = row.outcome.toLowerCase() === 'yes';
            const priceStr = d != null && d.price != null
              ? `${(d.price * 100).toFixed(2)}¢`
              : '—';
            const sharesStr = d != null && d.shares > 0
              ? d.shares >= 1 ? d.shares.toFixed(2) : d.shares.toString()
              : '—';
            return (
              <tr key={row.key}>
                <td className="orders-cell-side">{row.side}</td>
                <td className={`orders-cell-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}>
                  {row.outcome}
                </td>
                <td className="orders-cell-label">{row.label}</td>
                <td className="orders-cell-price">{priceStr}</td>
                <td className="orders-cell-shares">{sharesStr}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getOrderKeyForStatus(item, index) {
  if (!item) return null;
  const order = item.order || {};
  const marketId = item.marketId ?? 'unknown';
  const tokenId = order.tokenId ?? '0';
  return item.id || order.hash || `${marketId}-${tokenId}-${index}`;
}

function OpenOrdersList({ orders, markets, polymarketByQuestion, polymarketLoading, polymarketLinks, polyOrderbookByMarket, marketStartTimes, cancelBeforeStartByMarket, polyOutcomes, polyOutcomeOverrides, onSetPolyOverride, polyOptionsFromApi, polyViewMode, onSetPolyViewMode, onSavePolyLink, onDeletePolyLink, onSaveStartTime, onToggleCancelBeforeStart, onSavePolyOutcome, onReplaceAtBest, replaceOrderId, onRemoveOrder, removingOrderId, polyAutoOptions, partialFillAllBuys, partialFillOrderIds, cancelOnPartialFillEnabled, skipNotAtBestReminderOrderIds, telegramNotAtBest, onTogglePartialFillOrderId, onToggleSkipNotAtBestReminderId, autoRelist5MinByMarketId, onToggleAutoRelist5MinMarket, marketMakingByMarketId, onToggleMarketMakingMarket }) {
  const byMarket = useMemo(() => {
    const map = {};
    orders.forEach((item, idx) => {
      const id = item.marketId ?? 'unknown';
      const withIndex = { ...item, _orderIndex: idx };
      if (!map[id]) map[id] = [];
      map[id].push(withIndex);
    });
    return map;
  }, [orders]);

  const marketIds = useMemo(() => Object.keys(byMarket), [byMarket]);
  const [linkDrafts, setLinkDrafts] = useState({});
  const [startTimeDrafts, setStartTimeDrafts] = useState({});
  const [polyOutcomeDrafts, setPolyOutcomeDrafts] = useState({});
  const [savedMarketId, setSavedMarketId] = useState(null);
  const autoCancelHandledRef = useRef(new Set());
  const statusDragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const [statusPos, setStatusPos] = useState({ x: null, y: null });
  const orderRowRefs = useRef({});
  const prevOrderKeysRef = useRef(new Set());
  const highlightTimeoutsRef = useRef({});
  const [highlightedOrderKeys, setHighlightedOrderKeys] = useState({});
  const {
    autoCancelDanger = false,
    autoCancelBalancedLowBook = false,
    balancedLowBookMinShares = 3000,
    autoCancelOnPartialFill = false,
    priceSpreadThresholdCents = 0,
  } = polyAutoOptions || {};
  const partialFillIdSet = useMemo(
    () => new Set(Array.isArray(partialFillOrderIds) ? partialFillOrderIds : []),
    [partialFillOrderIds]
  );
  const skipNotAtBestIdSet = useMemo(
    () => new Set(Array.isArray(skipNotAtBestReminderOrderIds) ? skipNotAtBestReminderOrderIds : []),
    [skipNotAtBestReminderOrderIds]
  );
  const partialFillHandledRef = useRef(new Set());
  const cancelBeforeStartHandledRef = useRef(new Set()); // 已觸發「開賽前 1 小時撤銷該市場全部」的 marketId
  const [startCheckTick, setStartCheckTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStartCheckTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // 新掛單高亮：偵測新出現的訂單，10 秒內加上綠色背景
  useEffect(() => {
    const prevKeys = prevOrderKeysRef.current;
    const nextKeys = new Set();
    const newly = [];

    orders.forEach((item, idx) => {
      const key = getOrderKeyForStatus(item, idx);
      if (!key) return;
      nextKeys.add(key);
      if (!prevKeys.has(key)) newly.push(key);
    });

    prevOrderKeysRef.current = nextKeys;

    newly.forEach((key) => {
      setHighlightedOrderKeys((prev) => ({ ...prev, [key]: true }));
      if (highlightTimeoutsRef.current[key]) {
        clearTimeout(highlightTimeoutsRef.current[key]);
      }
      highlightTimeoutsRef.current[key] = setTimeout(() => {
        setHighlightedOrderKeys((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        delete highlightTimeoutsRef.current[key];
      }, 10000);
    });
  }, [orders]);

  // 卸載時清除計時器
  useEffect(() => () => {
    Object.values(highlightTimeoutsRef.current).forEach((t) => clearTimeout(t));
  }, []);

  // 懸浮窗：逐筆掛單的狀態列 + 是否在買一（依 orders 原始順序編號）
  const statusSummary = useMemo(() => {
    const items = [];
    const epsPrice = 0.0001;
    const epsBest = 0.001;
    const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;

    orders.forEach((item, idx) => {
      const market = markets[item.marketId] || {};
      const order = item.order || {};
      const tokenId = order.tokenId;

      // Predict 買一（依 outcome 對應的 orderbookByToken，否則退回整體 YES 盤口）
      const obByToken = market.orderbookByToken || {};
      const predictOb =
        tokenId && obByToken[tokenId]
          ? obByToken[tokenId]
          : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
      const pBids = predictOb.bidsTop3 || [];
      const pBuyOne = pBids[0];
      const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;

      // Polymarket 買一（多選項市場需依 outcome 如 Nuggets→DEN 取對應子市場）
      const outcomeName = tokenId != null ? (getOutcomeName(tokenId, market.outcomes) || '').trim() : '';
      const polyOb = getPolyOrderbookForOutcome(polyOrderbookByMarket, item.marketId, outcomeName);
      const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
      const polyBuyOne = polyBids[0];
      const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;

      let riskStatus = null;
      if (pBuyPrice != null && polyBuyPrice != null) {
        const diff = Math.abs(polyBuyPrice - pBuyPrice);
        if (spreadThreshold > 0 && diff <= spreadThreshold) riskStatus = 'safe';
        else if (polyBuyPrice - pBuyPrice > epsPrice) riskStatus = 'safe';
        else if (pBuyPrice - polyBuyPrice > epsPrice) riskStatus = 'danger';
        else riskStatus = 'balanced';
      }

      // 該筆訂單是否在買一（買單對比 bestBid、賣單對比 bestAsk）
      const tokenPrices = market.bestPricesByToken?.[tokenId] || {};
      const { bestBid, bestAsk } = tokenPrices;
      const isBuy = order.side === 0;
      const makerAmt = Number(order.makerAmount) || 0;
      const takerAmt = Number(order.takerAmount) || 0;
      const priceDecimal =
        isBuy
          ? takerAmt > 0 ? makerAmt / takerAmt : null
          : makerAmt > 0 ? takerAmt / makerAmt : null;
      let isAtBest = false;
      if (priceDecimal != null) {
        if (isBuy && bestBid != null && Math.abs(priceDecimal - bestBid) <= epsBest) isAtBest = true;
        if (!isBuy && bestAsk != null && Math.abs(priceDecimal - bestAsk) <= epsBest) isAtBest = true;
      }

      let statusText = '狀態未知';
      if (riskStatus === 'danger') statusText = '危險';
      else if (riskStatus === 'balanced') statusText = '均衡';
      else if (riskStatus === 'safe') statusText = '安全';

      const atBestText = isAtBest ? '' : '（該訂單不在買一）';
      const key = getOrderKeyForStatus(item, idx);

      items.push({
        key: key || item.id || `${item.marketId}-${tokenId}-${idx}`,
        orderKey: key,
        riskStatus,
        text: `${idx + 1}、${statusText}${atBestText}`,
      });
    });

    return items;
  }, [orders, markets, polyOrderbookByMarket, priceSpreadThresholdCents]);

  // 初始化懸浮窗位置（視窗右下角），並處理拖曳
  useEffect(() => {
    if (statusPos.x == null && typeof window !== 'undefined') {
      const margin = 24;
      const width = 260;
      const height = 140;
      setStatusPos({
        x: window.innerWidth - width - margin,
        y: window.innerHeight - height - margin,
      });
    }
  }, [statusPos.x, statusPos.y]);

  useEffect(() => {
    function handleMove(e) {
      if (!statusDragRef.current.dragging) return;
      const margin = 8;
      const maxX = (window.innerWidth || 0) - margin;
      const maxY = (window.innerHeight || 0) - margin;
      const nextX = e.clientX - statusDragRef.current.offsetX;
      const nextY = e.clientY - statusDragRef.current.offsetY;
      setStatusPos({
        x: Math.min(maxX, Math.max(margin, nextX)),
        y: Math.min(maxY, Math.max(margin, nextY)),
      });
    }
    function handleUp() {
      if (statusDragRef.current.dragging) {
        statusDragRef.current.dragging = false;
      }
    }
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  const handleStatusMouseDown = useCallback((e) => {
    // 只處理左鍵拖曳
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    statusDragRef.current = {
      dragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  }, []);

  const scrollToOrderByKey = useCallback((orderKey, align = 'center') => {
    if (!orderKey) return;
    const el = orderRowRefs.current?.[orderKey];
    if (el && typeof el.scrollIntoView === 'function') {
      const block = align === 'start' || align === 'end' ? align : 'center';
      el.scrollIntoView({ behavior: 'smooth', block });
    }
  }, []);

  const scrollToOrderIndex = useCallback((targetIndex, align = 'center') => {
    if (!orders || orders.length === 0) return;
    const safeIndex = Math.min(orders.length - 1, Math.max(0, targetIndex));
    const targetItem = orders[safeIndex];
    if (!targetItem) return;
    const key = getOrderKeyForStatus(targetItem, safeIndex);
    scrollToOrderByKey(key, align);
  }, [orders, scrollToOrderByKey]);

  const handleStatusItemClick = useCallback((s) => {
    if (!s?.orderKey) return;
    scrollToOrderByKey(s.orderKey, 'center');
  }, [scrollToOrderByKey]);

  // 監控訂單：有部分成交時立即撤掉該筆剩餘掛單（每筆只觸發一次）；僅買單；自選模式下僅勾選的買單
  useEffect(() => {
    if (!autoCancelOnPartialFill || !onRemoveOrder) return;
    const handled = partialFillHandledRef.current;
    orders.forEach((item) => {
      const order = item?.order || {};
      if (order.side !== 0) return; // 賣單不觸發部分成交撤單
      const id = item?.id || order.hash;
      if (!id || handled.has(id)) return;
      if (partialFillAllBuys === false && !partialFillIdSet.has(id)) return;
      const filled = Number(item.amountFilled) || 0;
      if (filled <= 0) return;
      handled.add(id);
      onRemoveOrder(item);
    });
  }, [autoCancelOnPartialFill, orders, onRemoveOrder, partialFillAllBuys, partialFillIdSet]);

  // 開賽前 1 小時撤銷該市場全部訂單（僅對已勾選「開賽前 1 小時自動撤單」的市場，依自訂開賽時間，每分鐘檢查，每個市場只觸發一次）
  useEffect(() => {
    if (!onRemoveOrder || !marketStartTimes || typeof marketStartTimes !== 'object' || !cancelBeforeStartByMarket) return;
    const ONE_HOUR_MS = 60 * 60 * 1000;
    const handled = cancelBeforeStartHandledRef.current;
    const now = Date.now();
    marketIds.forEach((marketId) => {
      if (!cancelBeforeStartByMarket[marketId]) return;
      const startStr = marketStartTimes[marketId];
      if (!startStr || handled.has(marketId)) return;
      const startMs = new Date(startStr.trim()).getTime();
      if (Number.isNaN(startMs)) return;
      if (now < startMs - ONE_HOUR_MS) return;
      handled.add(marketId);
      const list = byMarket[marketId] || [];
      list.forEach((item) => {
        if (item?.id) onRemoveOrder(item);
      });
    });
  }, [marketIds, byMarket, marketStartTimes, cancelBeforeStartByMarket, onRemoveOrder, startCheckTick]);

  // 依 Polymarket 比較結果自動撤單（危險 / 均衡且買一 shares 過低）
  useEffect(() => {
    if ((!autoCancelDanger && !autoCancelBalancedLowBook) || !onRemoveOrder) return;
    const handled = autoCancelHandledRef.current;

    marketIds.forEach((marketId) => {
      const market = markets[marketId] || {};
      const list = byMarket[marketId] || [];
      if (!list || list.length === 0) return;

      const obByToken = market.orderbookByToken || {};
      const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
      const eps = 0.0001;

      // 依 tokenId（outcome）分組：同市場雙邊掛單時，Yes/No 各自對應 Poly 一邊，只撤不符合的那一邊
      const byTokenId = new Map();
      list.forEach((item) => {
        const tid = item?.order?.tokenId;
        if (tid == null) return;
        if (!byTokenId.has(tid)) byTokenId.set(tid, []);
        byTokenId.get(tid).push(item);
      });

      byTokenId.forEach((itemsSameToken, tokenId) => {
        const outcomeName = (getOutcomeName(tokenId, market.outcomes) || '').trim();
        const predictOb =
          obByToken[tokenId]
            ? obByToken[tokenId]
            : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
        const pBids = predictOb.bidsTop3 || [];
        const pBuyOne = pBids[0];
        const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;

        const polyOb = getPolyOrderbookForOutcome(polyOrderbookByMarket, marketId, outcomeName);
        const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
        const polyBuyOne = polyBids[0];
        const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;
        const polyBuySize = polyBuyOne != null ? polyBuyOne[1] : 0;

        let polyBuyStatus = null;
        if (pBuyPrice != null && polyBuyPrice != null) {
          const diff = Math.abs(polyBuyPrice - pBuyPrice);
          if (spreadThreshold > 0 && diff <= spreadThreshold) {
            polyBuyStatus = 'safe';
          } else if (polyBuyPrice - pBuyPrice > eps) polyBuyStatus = 'safe';
          else if (pBuyPrice - polyBuyPrice > eps) polyBuyStatus = 'danger';
          else polyBuyStatus = 'balanced';
        }

        const shouldCancelForDanger = autoCancelDanger && polyBuyStatus === 'danger';
        const shouldCancelForBalancedLow =
          autoCancelBalancedLowBook &&
          polyBuyStatus === 'balanced' &&
          typeof polyBuySize === 'number' &&
          polyBuySize < (balancedLowBookMinShares || 3000);

        if (!shouldCancelForDanger && !shouldCancelForBalancedLow) return;

        itemsSameToken.forEach((item) => {
          const id = item?.id;
          if (!id || handled.has(id)) return;
          handled.add(id);
          onRemoveOrder(item);
        });
      });
    });
  }, [
    autoCancelDanger,
    autoCancelBalancedLowBook,
    balancedLowBookMinShares,
    priceSpreadThresholdCents,
    marketIds,
    byMarket,
    markets,
    polyOrderbookByMarket,
    onRemoveOrder,
  ]);

  return (
    <>
      <div className="orders-list">
        {marketIds.map((marketId) => {
        const market = markets[marketId] || {};
        const question = market.question || market.title || `市場 ${marketId}`;
        const list = byMarket[marketId] || [];
        const uniqueOrderTokenIds = [...new Set(list.map((i) => i?.order?.tokenId).filter((t) => t != null))];
        const linkUrl = linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '';
        const now = Date.now();
        const boostStart = market.boostStartsAt ? new Date(market.boostStartsAt).getTime() : null;
        const boostEnd = market.boostEndsAt ? new Date(market.boostEndsAt).getTime() : null;
        const inBoost = !!market.isBoosted && (!boostStart || now >= boostStart) && (!boostEnd || now <= boostEnd);
        const boostInMs = boostStart && boostStart > now ? boostStart - now : null;
        const boostInStr = boostInMs != null && !inBoost
          ? (() => {
              const h = Math.floor(boostInMs / 3600000);
              const m = Math.floor((boostInMs % 3600000) / 60000);
              return h > 0 ? `${h}h ${m}m` : `${m}m`;
            })()
          : null;
        const showBoost = inBoost || boostInStr;
        return (
          <div key={marketId} className="orders-market-block">
            <div className="orders-market-header">
              <div className="orders-market-icon">
                {market.imageUrl ? (
                  <img src={market.imageUrl} alt="" className="orders-market-icon-img" />
                ) : (
                  <span className="orders-market-icon-placeholder">
                    {question.slice(0, 1).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="orders-market-title-actions">
                <h3 className="orders-market-question" title={question}>
                  {question}
                </h3>
                <div className="orders-market-header-toggles">
                  <label className="positions-checkbox-label orders-market-toggle-label">
                    <input
                      type="checkbox"
                      checked={autoRelist5MinByMarketId[String(marketId)] !== false}
                      onChange={() => onToggleAutoRelist5MinMarket?.(marketId)}
                      title="仅买单：从勾选启用时刻起满 5 分钟首次重挂，之后每轮处理完该市场买单后再等 5 分钟（本页每 30 秒检查一次）。须保持标签页打开；Cloudflare 部署须填 Privy 私钥。"
                    />
                    5分钟定时重挂（仅买单）
                  </label>
                  <label className="positions-checkbox-label orders-market-toggle-label">
                    <input
                      type="checkbox"
                      checked={!!marketMakingByMarketId?.[String(marketId)]}
                      onChange={() => onToggleMarketMakingMarket?.(marketId)}
                      title="該市場全部掛單納入「做市」分頁；純做市 outcome 不輪詢 Poly"
                    />
                    做市
                  </label>
                </div>
              </div>
              {showBoost && (
                <span className="orders-boost-badge" title={inBoost ? '市場積分增強中' : `Boost 將在 ${boostInStr} 後開始`}>
                  {inBoost ? 'Boost' : `Boost in: ${boostInStr}`}
                </span>
              )}
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">Polymarket 市場連結</label>
              <input
                type="url"
                className="orders-poly-link-input"
                placeholder="https://polymarket.com/event/..."
                value={linkUrl}
                onChange={(e) => setLinkDrafts((prev) => ({ ...prev, [marketId]: e.target.value }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const url = (linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '').trim();
                  try {
                    await onSavePolyLink(marketId, url);
                    setLinkDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                    setSavedMarketId(marketId);
                    setTimeout(() => setSavedMarketId(null), 2500);
                  } catch (_) {}
                }}
              >
                保存
              </button>
              {savedMarketId === marketId && (
                <span className="orders-poly-link-saved">已保存</span>
              )}
              {polymarketLinks[marketId] && (
                <button
                  type="button"
                  className="orders-poly-link-delete"
                  onClick={async () => {
                    try {
                      await onDeletePolyLink(marketId);
                      setLinkDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                    } catch (_) {}
                  }}
                  title="刪除此 Poly 連結"
                >
                  删除
                </button>
              )}
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">Poly 子市場（選填）</label>
              <input
                type="text"
                className="orders-poly-link-input"
                placeholder="自動 或填 LIV、SUN、DRAW、ARS…"
                style={{ maxWidth: 160 }}
                value={polyOutcomeDrafts[marketId] ?? polyOutcomes?.[marketId] ?? ''}
                onChange={(e) => setPolyOutcomeDrafts((prev) => ({ ...prev, [marketId]: e.target.value }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const v = (polyOutcomeDrafts[marketId] ?? polyOutcomes?.[marketId] ?? '').toString().trim();
                  if (onSavePolyOutcome) await onSavePolyOutcome(marketId, v);
                  setPolyOutcomeDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                }}
              >
                保存
              </button>
              {(() => {
                const linkUrl = linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '';
                const opts = polyOptionsFromApi?.[marketId] || parsePolyMarketOptions(linkUrl);
                if (!opts || opts.length < 1) return null;
                const current = polyOutcomeOverrides?.[marketId] ?? polyOutcomes?.[marketId] ?? '';
                const currentNorm = current.toString().trim().toUpperCase();
                const viewMode = polyViewMode?.[marketId] || 'yes';
                return (
                  <span className="predict-best-force-btns" style={{ marginLeft: 12 }}>
                    <span className="positions-query-desc" style={{ marginRight: 4 }}>強制顯示：</span>
                    {opts.map((opt) => {
                      const o = String(opt).trim().toUpperCase();
                      return (
                        <button
                          key={o}
                          type="button"
                          className={`predict-force-btn ${currentNorm === o ? 'active' : ''}`}
                          onClick={() => onSetPolyOverride?.(marketId, currentNorm === o ? '' : o)}
                          title={currentNorm === o ? `當前 ${o}，點擊清除` : `顯示 ${o}`}
                        >
                          {o}
                        </button>
                      );
                    })}
                    {onSetPolyViewMode && (
                      <>
                        <span className="predict-force-sep">|</span>
                        <button
                          type="button"
                          className={`predict-force-btn ${viewMode === 'yes' ? 'active' : ''}`}
                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'yes' ? '' : 'yes')}
                          title="只看 Yes"
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className={`predict-force-btn ${viewMode === 'no' ? 'active' : ''}`}
                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'no' ? '' : 'no')}
                          title="只看 No"
                        >
                          No
                        </button>
                      </>
                    )}
                  </span>
                );
              })()}
            </div>
            <div className="orders-poly-link-row">
              <label className="orders-poly-link-label">開賽時間</label>
              <input
                type="datetime-local"
                className="orders-poly-link-input"
                style={{ maxWidth: 220 }}
                value={(startTimeDrafts[marketId] ?? marketStartTimes[marketId] ?? market?.startTime ?? '').toString().slice(0, 16)}
                onChange={(e) => setStartTimeDrafts((prev) => ({ ...prev, [marketId]: e.target.value || undefined }))}
              />
              <button
                type="button"
                className="orders-poly-link-save"
                onClick={async () => {
                  const v = (startTimeDrafts[marketId] ?? marketStartTimes[marketId] ?? market?.startTime ?? '').toString().trim().slice(0, 16);
                  const iso = v || '';
                  if (onSaveStartTime) await onSaveStartTime(marketId, iso);
                  setStartTimeDrafts((prev) => ({ ...prev, [marketId]: undefined }));
                }}
              >
                保存
              </button>
              <span className="positions-query-desc" style={{ marginLeft: 4 }}>留空則不自動撤單</span>
            </div>
            <div className="orders-poly-link-row">
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!cancelBeforeStartByMarket?.[marketId]}
                  onChange={(e) => onToggleCancelBeforeStart && onToggleCancelBeforeStart(marketId, e.target.checked)}
                />
                開賽前 1 小時自動撤銷該市場全部訂單（需已填開賽時間）
              </label>
            </div>
            <div className="orders-table-wrap">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Side</th>
                    <th>Outcome</th>
                    <th>Price</th>
                    <th>Shares</th>
                    <th>Filled</th>
                    <th>Value</th>
                    <th>Expiry</th>
                    <th title="部分成交时撤剩余挂单（仅买单；卖单永不启用）">部分成交撤单</th>
                    <th title="不在买一价时不再推送企业微信／Telegram 提醒（仅买单）">买一静默</th>
                    <th className="orders-cell-action-col">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => {
                    const order = item.order || {};
                    const isBuy = order.side === 0;
                    const side = isBuy ? 'Buy' : 'Sell';
                    const tokenId = order.tokenId;
                    const outcomeName = getOutcomeName(tokenId, market.outcomes);
                    const isYes = String(outcomeName).toLowerCase() === 'yes';
                    const makerAmt = Number(order.makerAmount) || 0;
                    const takerAmt = Number(order.takerAmount) || 0;
                    // 價格 = USDT / shares (0-1)
                    // Buy: maker=USDT, taker=shares => price = maker/taker
                    // Sell: maker=shares, taker=USDT => price = taker/maker
                    const priceDecimal =
                      isBuy
                        ? takerAmt > 0 ? makerAmt / takerAmt : null
                        : makerAmt > 0 ? takerAmt / makerAmt : null;
                    const priceCents =
                      priceDecimal != null ? priceDecimal * 100 : null;
                    const priceStr =
                      priceCents != null && !Number.isNaN(priceCents)
                        ? `${priceCents.toFixed(1)}¢`
                        : '—';
                    // 檢查是否在買一/賣一
                    // 根據 tokenId 取得該 outcome 的 bestBid/bestAsk
                    const tokenPrices = market.bestPricesByToken?.[tokenId] || {};
                    const { bestBid, bestAsk } = tokenPrices;
                    let notAtBest = false;
                    if (priceDecimal != null) {
                      if (isBuy && bestBid != null) {
                        // 買單：價格應等於 bestBid 才算買一
                        notAtBest = Math.abs(priceDecimal - bestBid) > 0.001;
                      } else if (!isBuy && bestAsk != null) {
                        // 賣單：價格應等於 bestAsk 才算賣一
                        notAtBest = Math.abs(priceDecimal - bestAsk) > 0.001;
                      }
                    }
                    const amountNum = Number(item.amount) || 0;
                    const amountHuman =
                      amountNum >= 1e10 ? amountNum / 1e18 : amountNum;
                    const sharesStr =
                      amountNum > 0
                        ? amountHuman >= 1
                          ? amountHuman.toFixed(2)
                          : amountHuman.toString()
                        : '—';
                    const filledNum = Number(item.amountFilled) || 0;
                    const filledHuman =
                      filledNum >= 1e10 ? filledNum / 1e18 : filledNum;
                    const filledStr =
                      filledNum > 0 ? filledHuman.toFixed(2) : '0';
                    const valueNum =
                      priceCents != null && amountHuman > 0
                        ? (priceCents / 100) * amountHuman
                        : null;
                    const valueStr =
                      valueNum != null && !Number.isNaN(valueNum)
                        ? `$${valueNum.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : '—';
                    const exp = order.expiration;
                    const expiryStr =
                      exp && Number(exp) > 0
                        ? (() => {
                            const d = new Date(Number(exp) * 1000);
                            return Number.isNaN(d.getTime())
                              ? '—'
                              : d.toLocaleDateString();
                          })()
                        : '—';
                    const sellLink = `https://predict.fun/market/${marketId}`;
                    const currentBest = isBuy ? bestBid : bestAsk;
                    const currentBestStr = currentBest != null ? `${(currentBest * 100).toFixed(1)}¢` : '—';
                    const outcomeTrim = (String(outcomeName).trim());
                    const rowPolyData =
                      polymarketLinks?.[marketId] &&
                      polymarketByQuestion &&
                      ((outcomeTrim && polymarketByQuestion[`${marketId}::${question}::${outcomeTrim}`]) ||
                        polymarketByQuestion[`${marketId}::${question}`]);
                    // 比較 Predict 掛單價格與 Poly 買一/賣一（依該筆訂單 outcome 取對應 Poly 子市場）
                    let polySafeStatus = null;
                    if (rowPolyData && priceDecimal != null) {
                      const polyKey = `${isBuy ? 'buy' : 'sell'}${isYes ? 'Yes' : 'No'}`;
                      const polyEntry = rowPolyData[polyKey];
                      if (polyEntry && polyEntry.price != null) {
                        const polyPrice = polyEntry.price;
                        const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
                        const diff = Math.abs(priceDecimal - polyPrice);
                        if (spreadThreshold > 0 && diff <= spreadThreshold) {
                          polySafeStatus = 'safe';
                        } else if (isBuy) {
                          polySafeStatus = priceDecimal < polyPrice ? 'safe' : 'danger';
                        } else {
                          polySafeStatus = priceDecimal > polyPrice ? 'safe' : 'danger';
                        }
                      }
                    }
                    const rowKey = getOrderKeyForStatus(item, item._orderIndex ?? 0);
                    const rowOrderId = item.id || order.hash;
                    const isHighlighted = rowKey && highlightedOrderKeys[rowKey];
                    return (
                      <tr
                        ref={(el) => {
                          if (!rowKey || !el) return;
                          orderRowRefs.current[rowKey] = el;
                        }}
                        data-order-key={rowKey || undefined}
                        key={rowKey || item.id || order.hash || Math.random()}
                        className={`${notAtBest ? 'orders-row-not-best' : ''} ${isHighlighted ? 'orders-row-new' : ''}`}
                        title={notAtBest ? `不在${isBuy ? '買一' : '賣一'}，當前${outcomeName} ${isBuy ? '買一' : '賣一'}=${currentBestStr}` : ''}
                      >
                        <td className="orders-cell-side">{side}</td>
                        <td
                          className={`orders-cell-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}
                        >
                          {outcomeName}
                        </td>
                        <td className="orders-cell-price">
                          {priceStr}
                          {notAtBest && <span className="orders-not-best-badge">!</span>}
                        </td>
                        <td className="orders-cell-shares">{sharesStr}</td>
                        <td className="orders-cell-filled">{filledStr}</td>
                        <td className="orders-cell-value">{valueStr}</td>
                        <td className="orders-cell-expiry">{expiryStr}</td>
                        <td className="orders-cell-pref">
                          {!isBuy ? (
                            <span className="positions-query-desc" title="部分成交撤单仅适用于买单，卖单不触发">卖单不适用</span>
                          ) : !cancelOnPartialFillEnabled ? (
                            <span className="positions-query-desc" title="请先勾选上方「当订单有部分成交时，立即撤掉该笔剩余挂单」">先开启选项</span>
                          ) : partialFillAllBuys !== false ? (
                            <span className="positions-query-desc" title="当前为「全部买单」模式">全部买单</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={rowOrderId ? partialFillIdSet.has(rowOrderId) : false}
                              disabled={!rowOrderId}
                              onChange={(e) => rowOrderId && onTogglePartialFillOrderId?.(rowOrderId, e.target.checked)}
                              title="自选：勾选后该买单才适用部分成交撤单"
                            />
                          )}
                        </td>
                        <td className="orders-cell-pref">
                          {!isBuy ? (
                            <span className="positions-query-desc" title="买一提醒静默仅适用于买单">卖单不适用</span>
                          ) : !telegramNotAtBest ? (
                            <span className="positions-query-desc" title="请先在机器人配置中开启「买一卖一提醒」">—</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={rowOrderId ? skipNotAtBestIdSet.has(rowOrderId) : false}
                              disabled={!rowOrderId}
                              onChange={(e) => rowOrderId && onToggleSkipNotAtBestReminderId?.(rowOrderId, e.target.checked)}
                              title="勾选后：该买单不在买一价时不推送提醒"
                            />
                          )}
                        </td>
                        <td className="orders-cell-action">
                          {polySafeStatus && (
                            <span
                              className={`orders-poly-status orders-poly-status-${polySafeStatus}`}
                              title={polySafeStatus === 'safe' ? '與 Poly 比較：安全' : '與 Poly 比較：危險'}
                            >
                              {polySafeStatus === 'safe' ? '安全' : '危險'}
                            </span>
                          )}
                          {notAtBest && onReplaceAtBest && (
                            <button
                              type="button"
                              className="orders-replace-btn"
                              title={`一鍵撤單並掛到當前${isBuy ? '買一' : '賣一'}（${currentBestStr}）`}
                              disabled={replaceOrderId === item.id}
                              onClick={() =>
                                onReplaceAtBest({
                                  orderId: item.id,
                                  marketId,
                                  tokenId: order.tokenId,
                                  side: order.side,
                                  sharesWei: item.amount,
                                })
                              }
                            >
                              {replaceOrderId === item.id ? '處理中…' : `一鍵撤單並掛到${isBuy ? '買一' : '賣一'}`}
                            </button>
                          )}
                          {onRemoveOrder && (
                            <button
                              type="button"
                              className="orders-remove-btn"
                              title="直接撤銷此筆掛單"
                              disabled={removingOrderId === item.id}
                              onClick={() => onRemoveOrder(item)}
                            >
                              {removingOrderId === item.id ? '撤單中…' : '撤單'}
                            </button>
                          )}
                          <a
                            href={sellLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="orders-cancel-link"
                            title="前往 Predict 取消／管理"
                          >
                            取消
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(() => {
              const tidList = uniqueOrderTokenIds.length > 0 ? uniqueOrderTokenIds : [];
              const obByToken = market.orderbookByToken || {};
              const rows = tidList.map((tid, depthIdx) => {
                const outcomeName = tid != null ? (getOutcomeName(tid, market.outcomes) || '').trim() : '';
                const predictOb =
                  tid && obByToken[tid]
                    ? obByToken[tid]
                    : { asksTop3: market.asksTop3 || [], bidsTop3: market.bidsTop3 || [] };
                const pAsks = predictOb.asksTop3 || [];
                const pBids = predictOb.bidsTop3 || [];
                const pSellOne = pAsks[0];
                const pBuyOne = pBids[0];
                const pSellPrice = pSellOne != null ? pSellOne[0] : null;
                const pSellSize = pSellOne != null ? pSellOne[1] : 0;
                const pBuyPrice = pBuyOne != null ? pBuyOne[0] : null;
                const pBuySize = pBuyOne != null ? pBuyOne[1] : 0;
                const pSellCents = pSellPrice != null ? (pSellPrice * 100).toFixed(1) : null;
                const pBuyCents = pBuyPrice != null ? (pBuyPrice * 100).toFixed(1) : null;
                const pHasDepth = pSellOne != null || pBuyOne != null;
                const pSellSizeStr =
                  typeof pSellSize === 'number'
                    ? pSellSize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                    : String(pSellSize);
                const pBuySizeStr =
                  typeof pBuySize === 'number'
                    ? pBuySize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                    : String(pBuySize);

                const polyOb = getPolyOrderbookForOutcome(polyOrderbookByMarket, marketId, outcomeName);
                const polyAsks = (polyOb?.asks || []).slice().sort((a, b) => (a[0] || 0) - (b[0] || 0));
                const polyBids = (polyOb?.bids || []).slice().sort((a, b) => (b[0] || 0) - (a[0] || 0));
                const polySellOne = polyAsks[0];
                const polyBuyOne = polyBids[0];
                const polySellPrice = polySellOne != null ? polySellOne[0] : null;
                const polySellSize = polySellOne != null ? polySellOne[1] : 0;
                const polyBuyPrice = polyBuyOne != null ? polyBuyOne[0] : null;
                const polyBuySize = polyBuyOne != null ? polyBuyOne[1] : 0;
                const polySellCents = polySellPrice != null ? (polySellPrice * 100).toFixed(1) : null;
                const polyBuyCents = polyBuyPrice != null ? (polyBuyPrice * 100).toFixed(1) : null;
                const polyHasDepth = polySellOne != null || polyBuyOne != null;
                const polySellSizeStr =
                  typeof polySellSize === 'number'
                    ? polySellSize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                    : String(polySellSize);
                const polyBuySizeStr =
                  typeof polyBuySize === 'number'
                    ? polyBuySize.toLocaleString(undefined, { maximumFractionDigits: 1 })
                    : String(polyBuySize);

                let polyBuyStatus = null;
                if (pBuyPrice != null && polyBuyPrice != null) {
                  const eps = 0.0001;
                  const spreadThreshold = (priceSpreadThresholdCents || 0) / 100;
                  const diff = Math.abs(polyBuyPrice - pBuyPrice);
                  if (spreadThreshold > 0 && diff <= spreadThreshold) polyBuyStatus = 'safe';
                  else if (polyBuyPrice - pBuyPrice > eps) polyBuyStatus = 'safe';
                  else if (pBuyPrice - polyBuyPrice > eps) polyBuyStatus = 'danger';
                  else polyBuyStatus = 'balanced';
                }

                if (!pHasDepth && !polyHasDepth) return null;

                return (
                  <div key={String(tid)} className="predict-best-row">
                    {pHasDepth && (
                      <div className="predict-best-col">
                        <span className="predict-best-label">
                          Predict 買一／賣一{outcomeName ? `（${outcomeName}）` : ''}
                        </span>
                        <div className="predict-best-table">
                          <div className="predict-best-tr predict-best-ask">
                            <span className="predict-best-td-label">賣一</span>
                            <span className="predict-best-td-price">
                              {pSellCents != null ? `${pSellCents}¢` : '—'}
                            </span>
                            <span className="predict-best-td-shares">
                              {pSellOne != null ? pSellSizeStr : '—'}
                            </span>
                          </div>
                          <div className="predict-best-tr predict-best-bid">
                            <span className="predict-best-td-label">買一</span>
                            <span className="predict-best-td-price">
                              {pBuyCents != null ? `${pBuyCents}¢` : '—'}
                            </span>
                            <span className="predict-best-td-shares">
                              {pBuyOne != null ? pBuySizeStr : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    {polyHasDepth && (
                      <div className="predict-best-col">
                        <div className="predict-best-label-row">
                          <span className="predict-best-label">
                            Polymarket 買一／賣一{outcomeName ? `（${outcomeName}）` : ''}
                          </span>
                          {depthIdx === 0
                            ? (() => {
                                const opts = polyOptionsFromApi?.[marketId] || parsePolyMarketOptions(linkDrafts[marketId] ?? polymarketLinks[marketId] ?? '');
                                const current = polyOutcomeOverrides?.[marketId] ?? polyOutcomes?.[marketId] ?? '';
                                const currentNorm = current.toString().trim().toUpperCase();
                                const viewMode = polyViewMode?.[marketId] || 'yes';
                                return (
                                  <span className="predict-best-force-btns">
                                    {opts && opts.length >= 1 && onSetPolyOverride && opts.map((opt) => {
                                      const o = String(opt).trim().toUpperCase();
                                      return (
                                        <button
                                          key={o}
                                          type="button"
                                          className={`predict-force-btn ${currentNorm === o ? 'active' : ''}`}
                                          onClick={() => onSetPolyOverride(marketId, currentNorm === o ? '' : o)}
                                          title={currentNorm === o ? `當前顯示 ${o}，點擊清除` : `強制顯示 ${o}`}
                                        >
                                          {o}
                                        </button>
                                      );
                                    })}
                                    {opts && opts.length >= 1 && onSetPolyViewMode && (
                                      <>
                                        <span className="predict-force-sep">|</span>
                                        <button
                                          type="button"
                                          className={`predict-force-btn ${viewMode === 'yes' ? 'active' : ''}`}
                                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'yes' ? '' : 'yes')}
                                          title="只看 Yes 市場（多選項子市場時；二元 Yes/No 雙邊掛單時每邊已固定盤口）"
                                        >
                                          Yes
                                        </button>
                                        <button
                                          type="button"
                                          className={`predict-force-btn ${viewMode === 'no' ? 'active' : ''}`}
                                          onClick={() => onSetPolyViewMode(marketId, viewMode === 'no' ? '' : 'no')}
                                          title="只看 No 市場（多選項子市場時；二元 Yes/No 雙邊掛單時每邊已固定盤口）"
                                        >
                                          No
                                        </button>
                                      </>
                                    )}
                                  </span>
                                );
                              })()
                            : null}
                        </div>
                        <div className="predict-best-table">
                          <div className="predict-best-tr predict-best-ask">
                            <span className="predict-best-td-label">賣一</span>
                            <span className="predict-best-td-price">
                              {polySellCents != null ? `${polySellCents}¢` : '—'}
                            </span>
                            <span className="predict-best-td-shares">
                              {polySellOne != null ? polySellSizeStr : '—'}
                            </span>
                          </div>
                          <div className="predict-best-tr predict-best-bid">
                            <span className="predict-best-td-label">買一</span>
                            <span className="predict-best-td-price">
                              {polyBuyCents != null ? `${polyBuyCents}¢` : '—'}
                            </span>
                            <span className="predict-best-td-shares">
                              {polyBuyOne != null ? polyBuySizeStr : '—'}
                            </span>
                            {polyBuyStatus && (
                              <span className={`predict-best-status predict-best-status-${polyBuyStatus}`}>
                                {polyBuyStatus === 'safe' ? '安全' : polyBuyStatus === 'danger' ? '危險' : '均衡'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
              const filtered = rows.filter(Boolean);
              return filtered.length > 0 ? <>{filtered}</> : null;
            })()}
            {uniqueOrderTokenIds.map((tid) => {
              const oName = (getOutcomeName(tid, market.outcomes) || '').trim();
              const obItem = getPolyOrderbookForOutcome(polyOrderbookByMarket, marketId, oName);
              const polyDataOne =
                polymarketLinks?.[marketId] &&
                polymarketByQuestion &&
                ((oName && polymarketByQuestion[`${marketId}::${question}::${oName}`]) || polymarketByQuestion[`${marketId}::${question}`]);
              const listSameOutcome = list.filter((x) => String(x?.order?.tokenId) === String(tid));
              return (
                <PolymarketDepthInline
                  key={String(tid)}
                  polyData={polyDataOne}
                  polyOrderbookItem={obItem}
                  loading={polymarketLoading}
                  orderList={listSameOutcome.length ? listSameOutcome : list}
                  market={market}
                />
              );
            })}
          </div>
        );
        })}
      </div>
      {statusSummary.length > 0 && (
        <div
          className="orders-status-floating"
          style={statusPos.x != null ? { left: statusPos.x, top: statusPos.y } : undefined}
          onMouseDown={handleStatusMouseDown}
        >
          <div className="orders-status-title">掛單風險速覽</div>
          {orders.length > 0 && (
            <div className="orders-status-jump">
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(0, 'start')}
              >
                到底
              </button>
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(Math.floor(orders.length / 2))}
              >
                中間
              </button>
              <button
                type="button"
                className="orders-status-jump-btn"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => scrollToOrderIndex(orders.length - 1, 'end')}
              >
                到顶
              </button>
            </div>
          )}
          <ol className="orders-status-list">
            {statusSummary.map((s, idx) => (
              <li key={s.key || idx} className="orders-status-item">
                <span
                  className={`orders-status-pill orders-status-pill-${s.riskStatus || 'none'} ${s.orderKey ? 'orders-status-pill-clickable' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusItemClick(s);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {s.text}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </>
  );
}

export default function App() {
  const [mainPage, setMainPage] = useState(() => {
    if (typeof window !== 'undefined' && window.location.pathname === '/primary-arbitrage') return 'primaryArbitrage';
    if (typeof window !== 'undefined' && window.location.pathname === '/lp-arb') return 'lpArb';
    if (typeof window !== 'undefined' && window.location.pathname === '/global-info') return 'globalInfo';
    if (typeof window !== 'undefined' && window.location.pathname === '/convertible-bond') return 'convertibleBond';
    if (typeof window !== 'undefined' && window.location.pathname === '/volume-spike') return 'volumeSpike';
    if (typeof window !== 'undefined' && window.location.pathname === '/wanwu-meme') return 'wanwuMeme';
    if (typeof window !== 'undefined' && (window.location.pathname === '/bem-mining' || window.location.pathname === '/tapeout-nand')) return 'tapeoutNand';
    if (typeof window !== 'undefined' && window.location.pathname === '/mars-mining') return 'marsMining';
    if (typeof window !== 'undefined' && window.location.pathname === '/poly-options') return 'polyOptions';
    return 'positions';
  });
  const [apiKey, setApiKey] = useState('');
  const [address, setAddress] = useState('');
  const [remember, setRemember] = useState(true);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasQueried, setHasQueried] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');
  const [pollIntervalSec, setPollIntervalSec] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_POLL), 10);
      return Number.isFinite(v) && POLL_OPTIONS.some((o) => o.value === v) ? v : 0;
    } catch {
      return 0;
    }
  });
  const [polyPollIntervalSec, setPolyPollIntervalSec] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(STORAGE_POLY_POLL), 10);
      return Number.isFinite(v) && POLY_POLL_OPTIONS.some((o) => o.value === v) ? v : 3;
    } catch {
      return 3;
    }
  });
  const [orders, setOrders] = useState([]);
  const [ordersMarkets, setOrdersMarkets] = useState({});
  const [ordersPolyOrderbook, setOrdersPolyOrderbook] = useState({});
  const [ordersError, setOrdersError] = useState(null);
  const [replaceOrderId, setReplaceOrderId] = useState(null);
  const [removingOrderId, setRemovingOrderId] = useState(null);
  const [autoRelist5MinByMarketId, setAutoRelist5MinByMarketId] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_AUTORELIST5_MARKET_IDS);
      const o = raw ? JSON.parse(raw) : {};
      return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  });
  const [autoRelistNextAtByMarketId, setAutoRelistNextAtByMarketId] = useState(loadAutoRelistNextAtFromStorage);
  const [marketMakingByMarketId, setMarketMakingByMarketId] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_MM_MARKET_IDS);
      const o = raw ? JSON.parse(raw) : {};
      return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
    } catch {
      return {};
    }
  });
  const mmOrderIdsMigratedRef = useRef(false);
  const [mmManualPriceCents, setMmManualPriceCents] = useState({});

  /** 舊版 predict_mm_order_ids → 依目前訂單還原為市場級做市勾選（一次性） */
  useEffect(() => {
    if (mmOrderIdsMigratedRef.current) return;
    try {
      const rawOld = localStorage.getItem(STORAGE_MM_ORDER_IDS);
      if (!rawOld) {
        mmOrderIdsMigratedRef.current = true;
        return;
      }
      const arr = JSON.parse(rawOld);
      if (!Array.isArray(arr) || arr.length === 0) {
        localStorage.removeItem(STORAGE_MM_ORDER_IDS);
        mmOrderIdsMigratedRef.current = true;
        return;
      }
      if (!orders.length) return;

      const oldSet = new Set(arr.map(String));
      const mids = new Set();
      orders.forEach((o) => {
        if (o?.id != null && oldSet.has(String(o.id)) && o.marketId != null && o.marketId !== '') {
          mids.add(String(o.marketId));
        }
      });

      if (mids.size > 0) {
        setMarketMakingByMarketId((prev) => {
          const next = { ...prev };
          mids.forEach((m) => {
            next[m] = true;
          });
          try {
            localStorage.setItem(STORAGE_MM_MARKET_IDS, JSON.stringify(next));
            localStorage.removeItem(STORAGE_MM_ORDER_IDS);
          } catch (_) {}
          return next;
        });
      } else {
        localStorage.removeItem(STORAGE_MM_ORDER_IDS);
      }
      mmOrderIdsMigratedRef.current = true;
    } catch (_) {
      mmOrderIdsMigratedRef.current = true;
    }
  }, [orders]);

  useEffect(() => {
    const mids = new Set();
    orders.forEach((o) => {
      const mid = o?.marketId;
      if (mid != null && mid !== '') mids.add(String(mid));
    });
    setAutoRelist5MinByMarketId((prev) => {
      const next = { ...prev };
      mids.forEach((mid) => {
        if (next[mid] === undefined) next[mid] = true;
      });
      Object.keys(next).forEach((k) => {
        if (!mids.has(k)) delete next[k];
      });
      try {
        localStorage.setItem(STORAGE_AUTORELIST5_MARKET_IDS, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, [orders]);

  /** 与开关同步：新出现且默认打开的市场，从「此刻」起算 5 分钟后首次重挂；已无挂单的市场删除 nextAt */
  useEffect(() => {
    const mids = new Set();
    orders.forEach((o) => {
      const mid = o?.marketId;
      if (mid != null && mid !== '') mids.add(String(mid));
    });
    setAutoRelistNextAtByMarketId((prevN) => {
      const n = { ...prevN };
      let changed = false;
      Object.keys(n).forEach((k) => {
        if (!mids.has(k)) {
          delete n[k];
          changed = true;
        }
      });
      const t = Date.now();
      mids.forEach((mid) => {
        if (autoRelist5MinByMarketId[mid] !== false && n[mid] == null) {
          n[mid] = t + AUTORELIST_5MIN_MS;
          changed = true;
        }
      });
      if (changed) return n;
      return prevN;
    });
  }, [orders, autoRelist5MinByMarketId]);

  /** 持久化「勾选起」时刻，刷新后仍按原计划计时 */
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_AUTORELIST5_NEXT_AT, JSON.stringify(autoRelistNextAtByMarketId));
    } catch (_) {}
  }, [autoRelistNextAtByMarketId]);

  useEffect(() => {
    const mids = new Set();
    orders.forEach((o) => {
      const mid = o?.marketId;
      if (mid != null && mid !== '') mids.add(String(mid));
    });
    setMarketMakingByMarketId((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.keys(next).forEach((k) => {
        if (!mids.has(k)) {
          delete next[k];
          changed = true;
        }
      });
      if (changed) {
        try {
          localStorage.setItem(STORAGE_MM_MARKET_IDS, JSON.stringify(next));
        } catch (_) {}
      }
      return changed ? next : prev;
    });
  }, [orders]);
  const [polymarketByQuestion, setPolymarketByQuestion] = useState({});
  const [polymarketLoading, setPolymarketLoading] = useState(false);
  const [polymarketError, setPolymarketError] = useState(null);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [polymarketLinks, setPolymarketLinks] = useState({});
  const [marketStartTimes, setMarketStartTimes] = useState({});
  const [cancelBeforeStartByMarket, setCancelBeforeStartByMarket] = useState({});
  const [polyOutcomes, setPolyOutcomes] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [telegramAlerts, setTelegramAlerts] = useState({
    orderPlaced: true,
    orderCancelled: true,
    orderFilled: true,
    orderDanger: true,
    notAtBest: true,
  });
  const [telegramPollSec, setTelegramPollSec] = useState(120);
  const [wecomWebhookUrl, setWecomWebhookUrl] = useState('');
  const [dingtalkWebhookUrl, setDingtalkWebhookUrl] = useState('');
  const [polyAutoCancel, setPolyAutoCancel] = useState({
    danger: false,
    balancedLowBook: false,
    balancedLowBookMinShares: 3000,
    cancelOnPartialFill: false,
    partialFillAllBuys: true,
    partialFillOrderIds: [],
    skipNotAtBestReminderOrderIds: [],
    priceSpreadThresholdCents: 0,
  });
  const [polyOutcomeOverrides, setPolyOutcomeOverrides] = useState({});
  const [polyOptionsFromApi, setPolyOptionsFromApi] = useState({});
  const [polyViewMode, setPolyViewMode] = useState({});

  // 當前帳戶備註
  const currentAccountNote = useMemo(() => {
    const addrLower = String(address || '').toLowerCase();
    if (!addrLower) return '';
    const found = savedAccounts.find(
      (a) => String(a.address || '').toLowerCase() === addrLower
    );
    return found?.note || '';
  }, [savedAccounts, address]);

  // 載入後端配置（API Key + 帳戶列表）
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const resp = await fetch(api('/api/config'));
        if (!resp.ok) return;
        const body = await resp.json();
        if (!cancelled && body?.success && body?.data) {
          const {
            apiKey: k,
            accounts: accts,
            currentAddress: curr,
            telegramAlerts: alerts,
            telegramPollIntervalSec: tSec,
          wecomWebhookUrl: wcUrl,
          dingtalkWebhookUrl: dtUrl,
            polyAutoCancel: polyCfg,
          } = body.data;
          setApiKey(k || '');
          setSavedAccounts(Array.isArray(accts) ? accts : []);
          if (tSec != null) {
            setTelegramPollSec(Number(tSec) || 120);
          }
          if (alerts && typeof alerts === 'object') {
            setTelegramAlerts((prev) => ({
              orderPlaced: true,
              orderCancelled: true,
              orderFilled: true,
              orderDanger: true,
              notAtBest: true,
              ...prev,
              ...alerts,
            }));
          }
        if (typeof wcUrl === 'string') setWecomWebhookUrl(wcUrl || '');
        if (typeof dtUrl === 'string') setDingtalkWebhookUrl(dtUrl || '');
          const addr = (curr || '').trim();
          setAddress(
            addr
              ? addr
              : Array.isArray(accts) && accts.length > 0
                ? accts[0].address || ''
                : ''
          );
          if (polyCfg && typeof polyCfg === 'object') {
            setPolyAutoCancel((prev) => ({
              danger: !!(polyCfg.danger ?? prev.danger),
              balancedLowBook: !!(polyCfg.balancedLowBook ?? prev.balancedLowBook),
              balancedLowBookMinShares:
                polyCfg.balancedLowBookMinShares != null
                  ? Number(polyCfg.balancedLowBookMinShares) || prev.balancedLowBookMinShares
                  : prev.balancedLowBookMinShares,
              cancelOnPartialFill: !!(polyCfg.cancelOnPartialFill ?? prev.cancelOnPartialFill),
              partialFillAllBuys: polyCfg.partialFillAllBuys !== false,
              partialFillOrderIds:
                polyCfg.partialFillOrderIds != null && Array.isArray(polyCfg.partialFillOrderIds)
                  ? polyCfg.partialFillOrderIds.map(String)
                  : (prev.partialFillOrderIds ?? []),
              skipNotAtBestReminderOrderIds:
                polyCfg.skipNotAtBestReminderOrderIds != null && Array.isArray(polyCfg.skipNotAtBestReminderOrderIds)
                  ? polyCfg.skipNotAtBestReminderOrderIds.map(String)
                  : (prev.skipNotAtBestReminderOrderIds ?? []),
              priceSpreadThresholdCents:
                polyCfg.priceSpreadThresholdCents != null
                  ? Math.max(0, Math.min(50, Math.floor(Number(polyCfg.priceSpreadThresholdCents) || 0)))
                  : prev.priceSpreadThresholdCents ?? 0,
            }));
          }
        }
      } catch (_) {
        try {
          const k = localStorage.getItem(STORAGE_API_KEY) || '';
          const a = localStorage.getItem(STORAGE_ADDRESS) || '';
          const accts = localStorage.getItem(STORAGE_ACCOUNTS);
          if (!cancelled) {
            setApiKey(k);
            setAddress(a);
            setSavedAccounts(accts ? JSON.parse(accts) : []);
          }
        } catch (__) {}
      } finally {
        if (!cancelled) setConfigLoaded(true);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // 同步到後端（API Key、帳戶、當前地址）
  const syncConfig = useCallback(async (updates) => {
    try {
      const resp = await fetch(api('/api/config'), { method: 'GET' });
      if (!resp.ok) return;
      const body = await resp.json();
      const config = body?.data || {
        apiKey: '',
        accounts: [],
        currentAddress: '',
        telegramAlerts: { orderPlaced: true, orderCancelled: true, orderFilled: true, orderDanger: true, notAtBest: true },
      telegramPollIntervalSec: undefined,
      wecomWebhookUrl: '',
      dingtalkWebhookUrl: '',
        polyAutoCancel: {},
      };
      const merged = {
        apiKey: updates.apiKey !== undefined ? updates.apiKey : config.apiKey,
        accounts: updates.accounts !== undefined ? updates.accounts : config.accounts,
        currentAddress: updates.currentAddress !== undefined ? updates.currentAddress : config.currentAddress,
        telegramAlerts: updates.telegramAlerts !== undefined ? updates.telegramAlerts : (config.telegramAlerts || { orderPlaced: true, orderCancelled: true, orderFilled: true, orderDanger: true, notAtBest: true }),
        telegramPollIntervalSec: updates.telegramPollIntervalSec !== undefined
          ? updates.telegramPollIntervalSec
          : (config.telegramPollIntervalSec !== undefined ? config.telegramPollIntervalSec : undefined),
      wecomWebhookUrl: updates.wecomWebhookUrl !== undefined ? updates.wecomWebhookUrl : (config.wecomWebhookUrl || ''),
      dingtalkWebhookUrl: updates.dingtalkWebhookUrl !== undefined ? updates.dingtalkWebhookUrl : (config.dingtalkWebhookUrl || ''),
        polyAutoCancel:
          updates.polyAutoCancel !== undefined
            ? { ...(config.polyAutoCancel || {}), ...updates.polyAutoCancel }
            : config.polyAutoCancel,
      };
      await fetch(api('/api/config'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(merged),
      });
    } catch (_) {}
  }, []);

  const togglePartialFillOrderId = useCallback((orderId, enabled) => {
    const id = String(orderId || '');
    if (!id) return;
    setPolyAutoCancel((prev) => {
      const s = new Set(Array.isArray(prev.partialFillOrderIds) ? prev.partialFillOrderIds : []);
      if (enabled) s.add(id);
      else s.delete(id);
      const next = { ...prev, partialFillOrderIds: [...s] };
      if (remember) queueMicrotask(() => syncConfig({ polyAutoCancel: next }));
      return next;
    });
  }, [remember, syncConfig]);

  const toggleSkipNotAtBestReminderId = useCallback((orderId, enabled) => {
    const id = String(orderId || '');
    if (!id) return;
    setPolyAutoCancel((prev) => {
      const s = new Set(Array.isArray(prev.skipNotAtBestReminderOrderIds) ? prev.skipNotAtBestReminderOrderIds : []);
      if (enabled) s.add(id);
      else s.delete(id);
      const next = { ...prev, skipNotAtBestReminderOrderIds: [...s] };
      if (remember) queueMicrotask(() => syncConfig({ polyAutoCancel: next }));
      return next;
    });
  }, [remember, syncConfig]);

  // 載入 Polymarket 連結與開賽時間（單獨檔案，超過 7 天未使用已自動刪除）
  const loadPolyLinks = useCallback(async () => {
    try {
      const resp = await fetch(api('/api/polymarket-links'));
      if (!resp.ok) return;
      const body = await resp.json();
      if (body?.success && body?.data) {
        const d = body.data;
        if (d.links != null && typeof d.links === 'object') {
          setPolymarketLinks((prev) => mergePolyServerPayload(prev, d.links));
        }
        if (d.startTimes != null && typeof d.startTimes === 'object') {
          setMarketStartTimes((prev) => mergePolyServerPayload(prev, d.startTimes));
        }
        if (d.cancelBeforeStartByMarket != null && typeof d.cancelBeforeStartByMarket === 'object') {
          setCancelBeforeStartByMarket((prev) => mergePolyServerPayload(prev, d.cancelBeforeStartByMarket));
        }
        if (d.polyOutcomes != null && typeof d.polyOutcomes === 'object') {
          setPolyOutcomes((prev) => mergePolyServerPayload(prev, d.polyOutcomes));
        }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (configLoaded) loadPolyLinks();
  }, [configLoaded, loadPolyLinks]);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (apiKey.trim()) h['x-api-key'] = apiKey.trim();
    return h;
  }, [apiKey]);

  // Cloudflare 部署時：在瀏覽器內用 Privy 私鑰簽名取得 JWT，再查挂单（私鑰不經伺服器）
  // 回傳 { token } 或 { error: string } 方便顯示具體失敗原因
  const getJwtForOrders = useCallback(async (apiKeyVal, addressVal, privateKeyVal) => {
    if (!apiKeyVal?.trim() || !addressVal?.trim() || !privateKeyVal?.trim()) {
      return { error: '缺少 API Key、地址或 Privy 私鑰' };
    }
    try {
      const msgRes = await fetch(api('/api/auth/message'), { headers: { 'x-api-key': apiKeyVal.trim() } });
      const msgBody = await msgRes.json().catch(() => ({}));
      if (!msgRes.ok) {
        return { error: `取得簽名訊息失敗（${msgRes.status}）${msgBody?.message ? ': ' + msgBody.message : ''}` };
      }
      if (!msgBody?.data?.message) {
        return { error: '後端未回傳簽名用 message，請確認 Worker 已部署且 /api/auth/message 正常' };
      }
      let signature;
      try {
        signature = await signPredictAccountMessage(privateKeyVal.trim(), addressVal.trim(), msgBody.data.message);
      } catch (e) {
        return { error: `瀏覽器簽名失敗：${e?.message || '請確認私鑰格式為 0x 開頭 64 字元十六進位'}` };
      }
      const authRes = await fetch(api('/api/auth'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKeyVal.trim() },
        body: JSON.stringify({ signer: addressVal.trim(), signature, message: msgBody.data.message }),
      });
      const authBody = await authRes.json().catch(() => ({}));
      if (!authRes.ok) {
        return { error: `換取 JWT 失敗（${authRes.status}）${authBody?.message ? ': ' + authBody.message : ''}，請確認該私鑰對應此存款地址（Predict → Account → Settings → Export）` };
      }
      const token = jwtToString(authBody?.data?.token);
      if (!token) {
        return { error: '後端未回傳 token' };
      }
      return { token };
    } catch (e) {
      return { error: `JWT 取得異常：${e?.message || String(e)}` };
    }
  }, []);

  const toggleAutoRelist5MinMarket = useCallback((marketId) => {
    if (marketId == null || marketId === '') return;
    const id = String(marketId);
    setAutoRelist5MinByMarketId((prev) => {
      const isOn = prev[id] !== false;
      const next = { ...prev, [id]: isOn ? false : true };
      try {
        localStorage.setItem(STORAGE_AUTORELIST5_MARKET_IDS, JSON.stringify(next));
      } catch (_) {}
      queueMicrotask(() => {
        setAutoRelistNextAtByMarketId((prevN) => {
          const n = { ...prevN };
          if (isOn) {
            delete n[id];
          } else {
            n[id] = Date.now() + AUTORELIST_5MIN_MS;
          }
          return n;
        });
      });
      return next;
    });
  }, []);

  const toggleMarketMakingMarket = useCallback((marketId) => {
    if (marketId == null || marketId === '') return;
    const id = String(marketId);
    setMarketMakingByMarketId((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      try {
        localStorage.setItem(STORAGE_MM_MARKET_IDS, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, []);

  const signAndPostLimitOrder = useCallback(
    async (addr, placeFields, keyOverride, privateKeyOverride, guardMarket) => {
      const acc = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
      const key = (keyOverride || acc?.apiKey || apiKey || '').trim();
      const pkSource = privateKeyOverride || acc?.privateKey;
      if (!pkSource?.trim()) throw new Error('需在「已保存帳戶」填寫 Privy 私鑰');
      if (guardMarket && typeof guardMarket === 'object') assertPassiveLimitOrThrow(placeFields, guardMarket);
      const pk = String(pkSource).trim().startsWith('0x') ? String(pkSource).trim() : `0x${String(pkSource).trim()}`;
      const { OrderBuilder, ChainId, Side: SDKSide } = await import('@predictdotfun/sdk');
      const signer = new Wallet(pk);
      const builder = await OrderBuilder.make(ChainId.BnbMainnet, signer, { predictAccount: addr });
      const priceWei = BigInt(placeFields.pricePerShareWei);
      const quantityWei = BigInt(placeFields.sharesWei);
      const sideEnum = placeFields.side === 0 ? SDKSide.BUY : SDKSide.SELL;
      const { pricePerShare, makerAmount, takerAmount } = builder.getLimitOrderAmounts({
        side: sideEnum,
        pricePerShareWei: priceWei,
        quantityWei,
      });
      const orderBuilt = builder.buildOrder('LIMIT', {
        side: sideEnum,
        tokenId: String(placeFields.tokenId),
        makerAmount,
        takerAmount,
        nonce: 0n,
        feeRateBps: BigInt(Math.max(PREDICT_MIN_FEE_RATE_BPS, Math.floor(Number(placeFields.feeRateBps) || 0))),
      });
      const typedData = builder.buildTypedData(orderBuilt, {
        isNegRisk: !!placeFields.isNegRisk,
        isYieldBearing: !!placeFields.isYieldBearing,
      });
      const signedOrder = await builder.signTypedDataOrder(typedData);
      const hash = builder.buildTypedDataHash(typedData);
      const jwtResult = await getJwtForOrders(key, addr, pkSource.trim());
      if (jwtResult?.error) throw new Error(jwtResult.error);
      const jwtStr = jwtToString(jwtResult.token);
      if (!jwtStr) throw new Error('JWT 為空');
      const createRes = await fetch(api('/api/orders/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': key, Authorization: `Bearer ${jwtStr}` },
        body: JSON.stringify({
          data: {
            order: { ...signedOrder, hash },
            pricePerShare: String(pricePerShare),
            strategy: 'LIMIT',
          },
        }),
      });
      const createData = await createRes.json().catch(() => ({}));
      if (!createRes.ok) throw new Error(createData?.message || `掛單失敗 ${createRes.status}`);
      return jwtStr;
    },
    [savedAccounts, apiKey, getJwtForOrders]
  );

  const fetchPositions = useCallback(
    async (after = null, addrOverride = null) => {
      const addr = (addrOverride ?? address).trim();
      if (!addr) return { list: [], nextCursor: null };
      const url = after
        ? api(`/api/positions/${encodeURIComponent(addr)}?first=100&after=${encodeURIComponent(after)}`)
        : api(`/api/positions/${encodeURIComponent(addr)}?first=100`);
      const resp = await fetch(url, { headers: headers() });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.message || `持倉請求失敗 ${resp.status}`);
      }
      const body = await resp.json();
      const list = Array.isArray(body?.data) ? body.data : [];
      const nextCursor = body?.cursor ?? null;
      return { list, nextCursor };
    },
    [address, headers]
  );

  const fetchOrders = useCallback(async (addrOverride = null, jwt = null) => {
    const addr = (addrOverride ?? address).trim();
    if (!addr) return { list: [], markets: {}, polyOrderbook: {} };
    const token = jwtToString(jwt) || null;
    const orderHeaders = () => ({ ...headers(), ...(token ? { Authorization: `Bearer ${token}` } : {}) });
    // 先抓第 1 頁（Predict API 單次最多 100 筆），若有 cursor 再自動翻頁，最多抓到約 300 筆
    const base = api(`/api/orders/${encodeURIComponent(addr)}`);
    const firstPageUrl = `${base}?first=100`;
    const resp = await fetch(firstPageUrl, { headers: orderHeaders() });
    const body = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      throw new Error(body?.message || `掛單請求失敗 ${resp.status}`);
    }

    let list = Array.isArray(body?.data) ? body.data : [];
    let markets = body?.markets && typeof body.markets === 'object' ? body.markets : {};
    let polyOrderbook =
      body?.polyOrderbook && typeof body.polyOrderbook === 'object' ? body.polyOrderbook : {};
    let cursor = body?.cursor ?? null;

    // 追加後續頁面（若有），避免只看到前 100 筆掛單
    let safety = 0;
    while (cursor && list.length < 300 && safety < 5) {
      const pageUrl = `${base}?first=100&after=${encodeURIComponent(cursor)}`;
      // eslint-disable-next-line no-await-in-loop
      const resp2 = await fetch(pageUrl, { headers: orderHeaders() });
      // eslint-disable-next-line no-await-in-loop
      const body2 = await resp2.json().catch(() => ({}));
      if (!resp2.ok) break;
      const pageList = Array.isArray(body2?.data) ? body2.data : [];
      if (pageList.length === 0) break;
      list = list.concat(pageList);
      if (body2?.markets && typeof body2.markets === 'object') {
        markets = { ...markets, ...body2.markets };
      }
      if (body2?.polyOrderbook && typeof body2.polyOrderbook === 'object') {
        polyOrderbook = { ...polyOrderbook, ...body2.polyOrderbook };
      }
      cursor = body2?.cursor ?? null;
      safety += 1;
    }

    return { list, markets, polyOrderbook };
  }, [address, headers]);

  const savePolymarketLink = useCallback(async (marketId, url) => {
    const trimmed = (url || '').trim();
    const nextLinks = { ...polymarketLinks };
    if (trimmed) nextLinks[marketId] = trimmed;
    else delete nextLinks[marketId];
    const payload = { links: nextLinks };
    if (trimmed) {
      const parsedStart = parseStartTimeFromPolyUrl(trimmed);
      if (parsedStart && !(marketStartTimes[marketId] || '').trim()) {
        payload.startTimes = { ...marketStartTimes, [marketId]: parsedStart };
      }
    }
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data) {
      if (body.data.links) setPolymarketLinks(body.data.links);
      if (body.data.startTimes) setMarketStartTimes(body.data.startTimes);
      if (body.data.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
      if (body.data.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
      try {
        let refetchJwt = null;
        if (API_BASE) {
          const acc = savedAccounts.find((a) => (a.address || '').toLowerCase() === address.trim().toLowerCase());
          if (acc?.privateKey) {
            const jwtRes = await getJwtForOrders(apiKey.trim(), address.trim(), acc.privateKey);
            if (jwtRes?.token) refetchJwt = jwtToString(jwtRes.token) || null;
          }
        }
        const orderResult = await fetchOrders(address.trim(), refetchJwt);
        setOrders(orderResult?.list ?? []);
        setOrdersMarkets(orderResult?.markets ?? {});
        setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
      } catch (_) {}
    }
  }, [polymarketLinks, marketStartTimes, address, fetchOrders, headers, API_BASE, savedAccounts, apiKey, getJwtForOrders]);

  const deletePolymarketLink = useCallback(async (marketId) => {
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deleteMarketId: marketId }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.links) setPolymarketLinks(body.data.links);
    if (body?.success && body?.data?.startTimes) setMarketStartTimes(body.data.startTimes);
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
    if (body?.success && body?.data?.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
  }, []);

  const saveStartTime = useCallback(async (marketId, startTimeIso) => {
    const trimmed = typeof startTimeIso === 'string' ? startTimeIso.trim() : '';
    const next = { ...marketStartTimes };
    if (trimmed) next[marketId] = trimmed;
    else delete next[marketId];
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startTimes: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.startTimes) setMarketStartTimes(body.data.startTimes);
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
  }, [marketStartTimes]);

  const toggleCancelBeforeStart = useCallback(async (marketId, enabled) => {
    const next = { ...cancelBeforeStartByMarket };
    next[marketId] = !!enabled;
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cancelBeforeStartByMarket: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.cancelBeforeStartByMarket) setCancelBeforeStartByMarket(body.data.cancelBeforeStartByMarket);
  }, [cancelBeforeStartByMarket]);

  const savePolyOutcome = useCallback(async (marketId, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    const next = { ...polyOutcomes };
    if (trimmed) next[marketId] = trimmed;
    else delete next[marketId];
    const resp = await fetch(api('/api/polymarket-links'), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ polyOutcomes: next }),
    });
    const body = await resp.json().catch(() => ({}));
    if (body?.success && body?.data?.polyOutcomes) setPolyOutcomes(body.data.polyOutcomes);
    if (body?.success && address.trim()) {
      try {
        let refetchJwt = null;
        if (API_BASE) {
          const acc = savedAccounts.find((a) => (a.address || '').toLowerCase() === address.trim().toLowerCase());
          if (acc?.privateKey) {
            const jwtRes = await getJwtForOrders(apiKey.trim(), address.trim(), acc.privateKey);
            if (jwtRes?.token) refetchJwt = jwtToString(jwtRes.token) || null;
          }
        }
        const orderResult = await fetchOrders(address.trim(), refetchJwt);
        setOrders(orderResult?.list ?? []);
        setOrdersMarkets(orderResult?.markets ?? {});
        setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
      } catch (_) {}
    }
  }, [polyOutcomes, address, fetchOrders, API_BASE, savedAccounts, apiKey, getJwtForOrders]);

  const replaceAtBest = useCallback(
    async (params) => {
      const addr = address.trim();
      if (!addr || !apiKey.trim() || replaceOrderId) return;
      const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
      if (API_BASE && !accountWithKey?.privateKey?.trim()) {
        setOrdersError('一鍵撤單並掛到買一需在「已保存帳戶」填寫該帳戶的 Privy 私鑰（Predict → Account → Settings → Export）');
        setReplaceOrderId(null);
        return;
      }
      setReplaceOrderId(params.orderId);
      setOrdersError(null);
      try {
        let reqHeaders = { ...headers(), 'Content-Type': 'application/json' };
        if (API_BASE && accountWithKey?.privateKey) {
          const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
          if (jwtResult?.error) {
            setOrdersError(jwtResult.error);
            setReplaceOrderId(null);
            return;
          }
          {
            const jt = jwtToString(jwtResult?.token);
            if (jt) reqHeaders = { ...reqHeaders, Authorization: `Bearer ${jt}` };
          }
        }
        const resp = await fetch(api('/api/orders/replace-at-best'), {
          method: 'POST',
          headers: reqHeaders,
          body: JSON.stringify({
            address: addr,
            orderId: params.orderId,
            marketId: params.marketId,
            tokenId: params.tokenId,
            side: params.side,
            sharesWei: params.sharesWei,
          }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(body?.message || `操作失敗 ${resp.status}`);

        let jwtForRefetch = reqHeaders.Authorization?.replace(/^Bearer\s+/i, '') || null;
        // Cloudflare Worker 回傳 placeAtBest 時：在瀏覽器內簽名並 POST /api/orders/create（與 signAndPostLimitOrder 同一路徑）
        if (body?.placeAtBest) {
          const acc = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
          if (!acc?.privateKey?.trim()) {
            setOrdersError('一鍵掛到買一需在「已保存帳戶」填寫該帳戶的 Privy 私鑰');
            return;
          }
          jwtForRefetch = await signAndPostLimitOrder(
            addr,
            {
              tokenId: String(body.placeAtBest.tokenId),
              side: Number(body.placeAtBest.side),
              sharesWei: String(body.placeAtBest.sharesWei),
              pricePerShareWei: String(body.placeAtBest.pricePerShareWei),
              feeRateBps: Number(body.placeAtBest.feeRateBps) || 0,
              isNegRisk: !!body.placeAtBest.isNegRisk,
              isYieldBearing: !!body.placeAtBest.isYieldBearing,
            },
            null,
            null,
            ordersMarkets[params.marketId]
          );
        }

        const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr, jwtForRefetch);
        setOrders(list);
        setOrdersMarkets(mkt);
        setOrdersPolyOrderbook(po ?? {});
        setOrdersError(null);
      } catch (e) {
        const msg = e?.message || '一鍵撤單失敗';
        // 若後端回傳「目前無有效買一／賣一價格」，代表撤單已成功，但暫時無法自動掛到買一／賣一
        if (msg.includes('目前無有效買一／賣一價格')) {
          setOrdersError('已撤銷原掛單，但目前無有效買一／賣一價格，暫時無法自動掛單，請稍後再試。');
        } else if (msg.includes('掛單失敗') || msg.includes('操作失敗')) {
          setOrdersError('已撤單，但掛到買一／賣一失敗：' + (msg.includes('掛單失敗') ? msg : msg.replace(/^操作失敗\s*\d*\s*[，:]?/i, '')));
        } else {
          setOrdersError(msg);
        }
        // 無論是否掛單成功，都重新同步一次掛單列表（Cloudflare 需帶 JWT 否則 401）
        try {
          let refetchJwt = null;
          if (API_BASE) {
            const acc = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
            if (acc?.privateKey) {
              const jwtRes = await getJwtForOrders(apiKey.trim(), addr, acc.privateKey);
              if (jwtRes?.token) refetchJwt = jwtToString(jwtRes.token) || null;
            }
          }
          const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr, refetchJwt);
          setOrders(list);
          setOrdersMarkets(mkt);
          setOrdersPolyOrderbook(po ?? {});
        } catch (_) {
          // 若同步失敗，僅保留錯誤提示
        }
      } finally {
        setReplaceOrderId(null);
      }
    },
    [address, apiKey, headers, fetchOrders, replaceOrderId, API_BASE, savedAccounts, getJwtForOrders, signAndPostLimitOrder, ordersMarkets]
  );

  const removeOrder = useCallback(
    async (item) => {
      const addr = address.trim();
      const id = item?.id;
      if (!addr || !apiKey.trim() || !id || removingOrderId) return;
      setRemovingOrderId(id);
      setOrdersError(null);
      try {
        // 撤單需 JWT（Cloudflare 部署時前端用私鑰簽名取得）
        let orderHeaders = { ...headers() };
        const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
        if (accountWithKey?.privateKey) {
          const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
          {
            const jt = jwtToString(jwtResult?.token);
            if (jt) orderHeaders = { ...orderHeaders, Authorization: `Bearer ${jt}` };
          }
        }
        const resp = await fetch(api('/api/orders/remove'), {
          method: 'POST',
          headers: orderHeaders,
          body: JSON.stringify({ address: addr, ids: [id] }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(body?.message || `撤單失敗 ${resp.status}`);
        const jwtForRefetch = orderHeaders.Authorization?.replace(/^Bearer\s+/i, '') || null;
        const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr, jwtForRefetch);
        setOrders(list);
        setOrdersMarkets(mkt);
        setOrdersPolyOrderbook(po ?? {});
      } catch (e) {
        setOrdersError(e.message || '撤單失敗');
      } finally {
        setRemovingOrderId(null);
      }
    },
    [address, apiKey, headers, savedAccounts, fetchOrders, getJwtForOrders]
  );

  const removeOrderWithoutUi = useCallback(
    async (item, addr, existingJwt) => {
      const id = item?.id;
      if (!id) throw new Error('無訂單 id');
      let orderHeaders = { ...headers() };
      const existing = jwtToString(existingJwt);
      if (existing) orderHeaders.Authorization = `Bearer ${existing}`;
      else {
        const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
        if (accountWithKey?.privateKey) {
          const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
          const jt = jwtToString(jwtResult?.token);
          if (!jt) throw new Error(jwtResult?.error || '需要 JWT');
          orderHeaders.Authorization = `Bearer ${jt}`;
        }
      }
      const resp = await fetch(api('/api/orders/remove'), {
        method: 'POST',
        headers: orderHeaders,
        body: JSON.stringify({ address: addr, ids: [id] }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body?.message || `撤單失敗 ${resp.status}`);
      return orderHeaders.Authorization?.replace(/^Bearer\s+/i, '').trim() || null;
    },
    [headers, savedAccounts, apiKey, getJwtForOrders]
  );

  const replaceAtManualCents = useCallback(
    async (item, centsRaw) => {
      const cents = Math.floor(Number(centsRaw));
      if (!Number.isFinite(cents) || cents <= 0 || cents >= 100) {
        setOrdersError('請填寫有效價格（¢，1–99）');
        return;
      }
      const addr = address.trim();
      if (!addr || !apiKey.trim() || replaceOrderId) return;
      const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
      if (!accountWithKey?.privateKey?.trim()) {
        setOrdersError('加價掛單需填寫 Privy 私鑰');
        return;
      }
      setReplaceOrderId(item.id);
      setOrdersError(null);
      try {
        const market = ordersMarkets[item.marketId];
        const spec = orderItemToLimitParams(item, market, cents / 100);
        if (!spec) throw new Error('無法組單');
        let jwt = jwtToString((await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey))?.token);
        jwt = await removeOrderWithoutUi(item, addr, jwt || null);
        await signAndPostLimitOrder(addr, spec, null, null, market);
        const jr = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
        const { list, markets: mkt, polyOrderbook: po } = await fetchOrders(addr, jwtToString(jr?.token) || jwt || null);
        setOrders(list);
        setOrdersMarkets(mkt);
        setOrdersPolyOrderbook(po ?? {});
      } catch (e) {
        setOrdersError(e?.message || '加價掛單失敗');
      } finally {
        setReplaceOrderId(null);
      }
    },
    [
      address,
      apiKey,
      replaceOrderId,
      savedAccounts,
      ordersMarkets,
      getJwtForOrders,
      removeOrderWithoutUi,
      signAndPostLimitOrder,
      fetchOrders,
    ]
  );

  /** 定时重挂 tick 用：避免 setInterval 闭包过期 */
  const autoRelistTickRefs = useRef({
    orders: [],
    ordersMarkets: {},
    autoRelist5MinByMarketId: {},
    autoRelistNextAtByMarketId: {},
    replaceOrderId: null,
    address: '',
    apiKey: '',
    savedAccounts: [],
    headers: () => ({}),
    getJwtForOrders: async () => ({}),
    signAndPostLimitOrder: async () => '',
    fetchOrders: async () => ({ list: [], markets: {}, polyOrderbook: {} }),
  });
  /** 防止定时重挂 tick 重叠执行（重叠会对同一批单多次撤+挂，叠成多单） */
  const autoRelistTickInFlightRef = useRef(false);
  useEffect(() => {
    autoRelistTickRefs.current = {
      orders,
      ordersMarkets,
      autoRelist5MinByMarketId,
      autoRelistNextAtByMarketId,
      replaceOrderId,
      address,
      apiKey,
      savedAccounts,
      headers,
      getJwtForOrders,
      signAndPostLimitOrder,
      fetchOrders,
    };
  });

  // 定时重挂：从「勾选启用」起满 5 分钟后首次执行，之后每轮该市场处理完再延后 5 分钟（页内每 30s 检查）
  useEffect(() => {
    if (!hasQueried) return;
    const addr0 = address.trim();
    const key0 = apiKey.trim();
    if (!addr0 || !key0) return;
    const acc0 = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr0.toLowerCase());
    if (API_BASE && !acc0?.privateKey?.trim()) return;

    const id = setInterval(() => {
      void (async () => {
        if (autoRelistTickInFlightRef.current) return;
        autoRelistTickInFlightRef.current = true;
        try {
        const r = autoRelistTickRefs.current;
        if (r.replaceOrderId) return;
        const addr = r.address.trim();
        const key = r.apiKey.trim();
        if (!addr || !key) return;
        const accountWithKey = r.savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
        if (API_BASE && !accountWithKey?.privateKey?.trim()) return;

        const liveOrders = r.orders;
        const mkts = r.ordersMarkets;
        const arMap = r.autoRelist5MinByMarketId;
        const nextAtMap = r.autoRelistNextAtByMarketId || {};
        const now = Date.now();

        const buyByMarket = new Map();
        for (const o of liveOrders) {
          const mid = o?.marketId != null && o?.marketId !== '' ? String(o.marketId) : null;
          if (!mid || arMap[mid] === false || !o?.id) continue;
          if (Number(o.order?.side) !== 0) continue;
          if (!buyByMarket.has(mid)) buyByMarket.set(mid, []);
          buyByMarket.get(mid).push(o);
        }

        const dueMarkets = new Set();
        for (const mid of buyByMarket.keys()) {
          const t = nextAtMap[mid];
          if (typeof t === 'number' && now >= t) dueMarkets.add(mid);
        }
        for (const mid of Object.keys(nextAtMap)) {
          if (arMap[mid] === false) continue;
          if (buyByMarket.has(mid)) continue;
          const t = nextAtMap[mid];
          if (typeof t === 'number' && now >= t) dueMarkets.add(mid);
        }
        if (dueMarkets.size === 0) return;

        const bumps = {};
        for (const mid of dueMarkets) {
          const listRaw = buyByMarket.get(mid) || [];
          if (listRaw.length === 0) {
            bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
            continue;
          }
          // 每市场每轮只重挂一笔买单，避免同市场多单时轮流撤挂把单量越弄越多
          const list = [...listRaw].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
          const item = list[0];
          if (autoRelistTickRefs.current.replaceOrderId) break;
          try {
            let reqHeaders = { ...r.headers(), 'Content-Type': 'application/json' };
            let jt = null;
            if (API_BASE && accountWithKey?.privateKey) {
              const jwtResult = await r.getJwtForOrders(key, addr, accountWithKey.privateKey);
              if (jwtResult?.error) break;
              jt = jwtToString(jwtResult?.token);
              if (jt) reqHeaders = { ...reqHeaders, Authorization: `Bearer ${jt}` };
            }
            const fresh = await r.fetchOrders(addr, jt);
            const still = fresh.list.find((o) => String(o.id) === String(item.id));
            if (!still) {
              setOrders(fresh.list);
              setOrdersMarkets(fresh.markets);
              setOrdersPolyOrderbook(fresh.polyOrderbook ?? {});
              bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
              continue;
            }
            const marketFresh = fresh.markets[String(mid)] || fresh.markets[still.marketId] || {};
            const spec = orderItemToLimitParams(still, marketFresh);
            if (!spec) {
              bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
              continue;
            }
            try {
              assertPassiveLimitOrThrow(spec, marketFresh);
            } catch (err) {
              setOrdersError(err?.message || String(err));
              bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
              continue;
            }
            const resp = await fetch(api('/api/orders/replace-at-best'), {
              method: 'POST',
              headers: reqHeaders,
              body: JSON.stringify({
                address: addr,
                orderId: still.id,
                marketId: String(still.marketId),
                tokenId: spec.tokenId,
                side: spec.side,
                sharesWei: spec.sharesWei,
                pricePerShareWei: spec.pricePerShareWei,
              }),
            });
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) {
              setOrdersError(body?.message || `定时重挂失败 ${resp.status}`);
              bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
              continue;
            }
            let jwtForRefetch = reqHeaders.Authorization?.replace(/^Bearer\s+/i, '') || null;
            if (body?.placeAtBest) {
              if (!accountWithKey?.privateKey?.trim()) {
                setOrdersError('定时重挂需填写 Privy 私钥');
                bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
                continue;
              }
              try {
                assertPassiveLimitOrThrow(
                  {
                    tokenId: String(body.placeAtBest.tokenId),
                    side: Number(body.placeAtBest.side),
                    sharesWei: String(body.placeAtBest.sharesWei),
                    pricePerShareWei: String(body.placeAtBest.pricePerShareWei),
                    feeRateBps: Number(body.placeAtBest.feeRateBps) || 0,
                    isNegRisk: !!body.placeAtBest.isNegRisk,
                    isYieldBearing: !!body.placeAtBest.isYieldBearing,
                  },
                  marketFresh
                );
              } catch (err) {
                setOrdersError(err?.message || String(err));
                bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
                continue;
              }
              jwtForRefetch = await r.signAndPostLimitOrder(
                addr,
                {
                  tokenId: String(body.placeAtBest.tokenId),
                  side: Number(body.placeAtBest.side),
                  sharesWei: String(body.placeAtBest.sharesWei),
                  pricePerShareWei: String(body.placeAtBest.pricePerShareWei),
                  feeRateBps: Number(body.placeAtBest.feeRateBps) || 0,
                  isNegRisk: !!body.placeAtBest.isNegRisk,
                  isYieldBearing: !!body.placeAtBest.isYieldBearing,
                },
                null,
                null,
                marketFresh
              );
            }
            const orderRes = await r.fetchOrders(addr, jwtForRefetch);
            setOrders(orderRes.list);
            setOrdersMarkets(orderRes.markets);
            setOrdersPolyOrderbook(orderRes.polyOrderbook ?? {});
            setOrdersError(null);
          } catch (e) {
            setOrdersError(e?.message || '定时重挂异常');
          }
          bumps[mid] = Date.now() + AUTORELIST_5MIN_MS;
        }

        if (Object.keys(bumps).length > 0) {
          setAutoRelistNextAtByMarketId((prev) => ({ ...prev, ...bumps }));
        }
        } finally {
          autoRelistTickInFlightRef.current = false;
        }
      })();
    }, AUTORELIST_TICK_MS);

    return () => clearInterval(id);
  }, [hasQueried, address, apiKey, savedAccounts, API_BASE]);

  // API Key、帳戶或當前地址變更時同步到後端（定時重掛開關僅存本地，見 STORAGE_AUTORELIST5）
  useEffect(() => {
    if (!configLoaded || !remember) return;
    syncConfig({
      apiKey,
      accounts: savedAccounts,
      currentAddress: address,
      polyAutoCancel,
    });
  }, [configLoaded, remember, apiKey, savedAccounts, address, polyAutoCancel, syncConfig]);

  // 本地備份（後端不可用時）
  useEffect(() => {
    if (!remember) return;
    try {
      if (apiKey) localStorage.setItem(STORAGE_API_KEY, apiKey);
      else localStorage.removeItem(STORAGE_API_KEY);
      if (address) localStorage.setItem(STORAGE_ADDRESS, address);
      else localStorage.removeItem(STORAGE_ADDRESS);
      localStorage.setItem(STORAGE_ACCOUNTS, JSON.stringify(savedAccounts));
    } catch (_) {}
  }, [remember, apiKey, address, savedAccounts]);

  // 帳戶管理操作（useEffect 會自動 sync 到後端）
  const handleAddAccount = useCallback((newAcc) => {
    const exists = savedAccounts.some(
      (a) => a.address.toLowerCase() === newAcc.address.toLowerCase()
    );
    if (exists) return;
    setSavedAccounts((prev) => [
      ...prev,
      { id: generateId(), address: newAcc.address, note: newAcc.note || '', telegramEnabled: newAcc.telegramEnabled !== false },
    ]);
  }, [savedAccounts]);

  const handleUpdateAccount = useCallback((id, updates) => {
    setSavedAccounts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...updates } : a))
    );
  }, []);

  const loadQuery = useCallback(async (maybeAddrOverride) => {
    const addrOverride =
      typeof maybeAddrOverride === 'string' ? maybeAddrOverride : null;
    if (addrOverride != null && addrOverride !== '') setAddress(addrOverride);
    const key = apiKey.trim();
    const addr =
      addrOverride != null
        ? String(addrOverride).trim()
        : String(address || '').trim();
    if (!key || !addr) {
      setError('請填寫 API Key 與存款帳戶地址');
      return;
    }
    setLoading(true);
    setError(null);
    setOrdersError(null);
    setHasQueried(true);
    const LOAD_TIMEOUT_MS = 25000;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('請求逾時，請檢查網路或 API Key')), LOAD_TIMEOUT_MS)
    );
    try {
      const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
      let jwt = null;
      if (API_BASE) {
        if (!accountWithKey?.privateKey) {
          setOrdersError('此帳戶尚未填寫 Privy 私鑰，無法在 Cloudflare 部署下查挂单。請在「已保存帳戶」中點該帳戶的編輯，填寫 Privy 私鑰（Predict → Account → Settings → Export）後儲存，再重新查詢。');
        } else {
          const jwtResult = await getJwtForOrders(key, addr, accountWithKey.privateKey);
          if (jwtResult?.error) {
            setOrdersError(jwtResult.error);
          } else {
            jwt = jwtToString(jwtResult?.token) || null;
          }
        }
      }
      const [posResult, orderResult] = await Promise.race([
        Promise.all([
          fetchPositions(null, addr),
          fetchOrders(addr, jwt).catch((e) => {
            if (API_BASE && !jwt) {
              // 已在上面顯示「未填私鑰」或 JWT 取得失敗，不覆蓋
            } else {
              setOrdersError(e.message || '掛單載入失敗');
            }
            return { list: [], markets: {} };
          }),
        ]),
        timeoutPromise,
      ]);
      setAccount({ address: addr });
      setPositions(posResult.list);
      setCursor(posResult.nextCursor);
      setOrders(orderResult?.list ?? []);
      setOrdersMarkets(orderResult?.markets ?? {});
      setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
    } catch (e) {
      setError(e.message || '載入失敗');
      setAccount(null);
      setPositions([]);
      setCursor(null);
      setOrders([]);
      setOrdersMarkets({});
      setOrdersPolyOrderbook({});
    } finally {
      setLoading(false);
    }
  }, [apiKey, address, savedAccounts, fetchPositions, fetchOrders, getJwtForOrders]);

  const handleDeleteAccount = useCallback((id) => {
    setSavedAccounts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleSelectAccount = useCallback((acc) => {
    setAddress(acc.address);
    if (hasQueried && apiKey.trim() && acc?.address) loadQuery(acc.address);
  }, [hasQueried, apiKey, loadQuery]);

  const refetchData = useCallback(async () => {
    const addr = address.trim();
    if (!addr || !apiKey.trim()) return;
    const accountWithKey = savedAccounts.find((a) => (a.address || '').toLowerCase() === addr.toLowerCase());
    let jwt = null;
    if (API_BASE && accountWithKey?.privateKey) {
      const jwtResult = await getJwtForOrders(apiKey.trim(), addr, accountWithKey.privateKey);
      jwt = jwtToString(jwtResult?.token) || null;
    }
    try {
      const [posResult, orderResult] = await Promise.all([
        fetchPositions(null, addr),
        fetchOrders(addr, jwt).catch(() => ({ list: [], markets: {} })),
      ]);
      setAccount({ address: addr });
      setPositions(posResult.list);
      setCursor(posResult.nextCursor);
      setOrders(orderResult?.list ?? []);
      setOrdersMarkets(orderResult?.markets ?? {});
      setOrdersPolyOrderbook(orderResult?.polyOrderbook ?? {});
    } catch (_) {}
  }, [address, apiKey, savedAccounts, fetchPositions, fetchOrders, getJwtForOrders]);

  // 切換帳戶時自動載入（防抖 200ms，避免連點導致多次請求）
  useEffect(() => {
    if (!hasQueried || !apiKey.trim() || !address.trim()) return;
    const tid = setTimeout(() => loadQuery(), 200);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 僅在 address 變更時觸發
  }, [address]);

  useEffect(() => {
    if (!hasQueried || pollIntervalSec <= 0 || loading) return;
    const id = setInterval(refetchData, pollIntervalSec * 1000);
    return () => clearInterval(id);
  }, [hasQueried, pollIntervalSec, refetchData, loading]);

  // 掛單頁：依當前掛單市場與 outcome 請求 Polymarket 買一賣一（體育等多選項需依 outcome 如 Nuggets→DEN）
  const orderItems = useMemo(() => {
    const groups = new Map();
    orders.forEach((o) => {
      const id = o.marketId;
      if (!id) return;
      const m = ordersMarkets[id] || {};
      const question = m.question || m.title || '';
      if (!question) return;
      const outcomeName = getOutcomeName(o.order?.tokenId, m.outcomes);
      const outcome = (outcomeName || '').trim();
      const key = `${id}::${question}::${outcome}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(o);
    });
    const seen = new Set();
    const items = [];
    orders.forEach((o) => {
      const id = o.marketId;
      if (!id) return;
      const m = ordersMarkets[id] || {};
      const question = m.question || m.title || '';
      if (!question) return;
      const outcomeName = getOutcomeName(o.order?.tokenId, m.outcomes);
      const outcome = (outcomeName || '').trim();
      const key = `${id}::${question}::${outcome}`;
      if (seen.has(key)) return;
      const grp = groups.get(key) || [];
      const allMm =
        grp.length > 0 &&
        grp.every((x) => x.id && marketMakingByMarketId[String(x.marketId)]);
      if (allMm) return;
      seen.add(key);
      items.push({ marketId: id, question, outcome: outcome || undefined });
    });
    return items;
  }, [orders, ordersMarkets, marketMakingByMarketId]);

  const orderItemsKey = orderItems.map((i) => `${i.marketId}:${i.question}:${i.outcome || ''}`).sort().join('|');
  const lastPolyFetchRef = React.useRef(0);
  const prevPolyOverrideRef = React.useRef('');
  const polyPollIntervalMs = Math.max(1000, polyPollIntervalSec * 1000);
  const polyFetchMinIntervalMs = Math.max(600, polyPollIntervalMs * 0.85);
  const [polyPollTick, setPolyPollTick] = React.useState(0);

  useEffect(() => {
    if ((activeTab !== 'orders' && activeTab !== 'marketMaking') || orderItems.length === 0) return;
    const id = setInterval(() => setPolyPollTick((t) => t + 1), polyPollIntervalMs);
    return () => clearInterval(id);
  }, [activeTab, orderItems.length, polyPollIntervalMs]);

  useEffect(() => {
    if ((activeTab !== 'orders' && activeTab !== 'marketMaking') || orderItems.length === 0) {
      setPolymarketByQuestion({});
      setPolyOptionsFromApi({});
      return;
    }
    const overridesStr = JSON.stringify(polyOutcomeOverrides || {});
    if (prevPolyOverrideRef.current !== overridesStr) {
      prevPolyOverrideRef.current = overridesStr;
      lastPolyFetchRef.current = 0;
    }
    const now = Date.now();
    const hasData = Object.keys(polymarketByQuestion || {}).length > 0;
    const skipFreqRefresh = hasData && (now - lastPolyFetchRef.current < polyFetchMinIntervalMs) && Object.keys(polyOutcomeOverrides || {}).length === 0;
    let cancelled = false;
    if (!skipFreqRefresh) setPolymarketLoading(true);
    setPolymarketError(null);
    const tid = setTimeout(() => {
      fetch(api('/api/polymarket/by-questions'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(apiKey.trim() ? { 'x-api-key': apiKey.trim() } : {}) },
        body: JSON.stringify({
          items: orderItems,
          polymarketLinks,
          ...(Object.keys(polyOutcomeOverrides).length > 0 ? { polyOutcomeOverrides } : {}),
        }),
      })
        .then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body?.message || `請求失敗 ${r.status}`);
          return body;
        })
        .then((body) => {
          if (cancelled) return;
          lastPolyFetchRef.current = Date.now();
          if (body?.success && body?.data && typeof body.data === 'object') {
            setPolymarketByQuestion(body.data);
            if (body.polyOptions && typeof body.polyOptions === 'object') setPolyOptionsFromApi(body.polyOptions);
            setPolymarketError(null);
          } else if (!hasData) {
            setPolymarketByQuestion({});
            setPolymarketError(body?.message || '無法取得 Polymarket 數據');
          }
        })
        .catch((e) => {
          if (!cancelled && !hasData) {
            setPolymarketByQuestion({});
            setPolymarketError(e.message || 'Polymarket 請求失敗，請確認後端已啟動');
          }
        })
        .finally(() => {
          if (!cancelled) setPolymarketLoading(false);
        });
    }, hasData ? 800 : 0);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [activeTab, orderItemsKey, apiKey, JSON.stringify(polymarketLinks), JSON.stringify(polyOutcomeOverrides), polyPollTick]);

  // Cloudflare 部署時 Worker 不回傳 polyOrderbook，用 polymarketByQuestion 補齊（依 marketId 區分，僅有填寫 Poly 連結的市場才顯示）
  const effectivePolyOrderbook = useMemo(() => {
    const base = ordersPolyOrderbook && typeof ordersPolyOrderbook === 'object' ? { ...ordersPolyOrderbook } : {};
    if (!ordersMarkets || !polymarketByQuestion || typeof polymarketByQuestion !== 'object') return base;
    orders.forEach((item) => {
      const marketId = item.marketId;
      if (!polymarketLinks || !(polymarketLinks[marketId] || '').trim()) return;
      const m = ordersMarkets[marketId];
      const question = (m && (m.question || m.title)) || '';
      const outcomeName = getOutcomeName(item.order?.tokenId, m?.outcomes) || '';
      if (!question) return;
      const key = outcomeName ? `${marketId}::${outcomeName}` : marketId;
      // 僅依該 outcome 的 key 判斷是否已有盤口，避免僅有 marketId 時誤跳過另一邊 Yes/No
      if (base[key]?.bids?.length) return;
      const poly = (outcomeName && polymarketByQuestion[`${marketId}::${question}::${outcomeName}`]) || polymarketByQuestion[`${marketId}::${question}`];
      if (!poly) return;
      // 二元 Yes/No 依掛單 outcome 固定對應盤口；其餘維持 polyViewMode／名稱推斷
      const viewNo = getPolyViewNoForOutcome(outcomeName, polyViewMode, marketId);
      const hasRawYes = (Array.isArray(poly.bidsYes) && poly.bidsYes.length > 0) || (Array.isArray(poly.asksYes) && poly.asksYes.length > 0);
      const yesBids = Array.isArray(poly.bidsYes) && poly.bidsYes.length > 0 ? poly.bidsYes : (poly.buyYes != null ? [[Number(poly.buyYes.price) || 0, Number(poly.buyYes.shares) || 0]] : poly.bids || []);
      const yesAsks = Array.isArray(poly.asksYes) && poly.asksYes.length > 0 ? poly.asksYes : (poly.sellYes != null ? [[Number(poly.sellYes.price) || 0, Number(poly.sellYes.shares) || 0]] : poly.asks || []);
      const bids = viewNo && hasRawYes ? yesAsks.map(([p, s]) => [1 - p, s]).sort((a, b) => (b[0] || 0) - (a[0] || 0)) : yesBids;
      const asks = viewNo && hasRawYes ? yesBids.map(([p, s]) => [1 - p, s]).sort((a, b) => (a[0] || 0) - (b[0] || 0)) : yesAsks;
      if (bids.length || asks.length) {
        const target = outcomeName ? key : marketId;
        base[target] = { ...(base[target] || {}), bids: bids.length ? bids : (base[target]?.bids || []), asks: asks.length ? asks : (base[target]?.asks || []) };
      }
    });
    return base;
  }, [ordersPolyOrderbook, ordersMarkets, polymarketByQuestion, polymarketLinks, orders, polyViewMode]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_POLL, String(pollIntervalSec));
    } catch (_) {}
  }, [pollIntervalSec]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { list, nextCursor } = await fetchPositions(cursor, address.trim());
      setPositions((prev) => [...prev, ...list]);
      setCursor(nextCursor);
    } catch (e) {
      setError(e.message || '載入更多失敗');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, address, fetchPositions]);

  return (
    <div className="positions-page">
      <div className="main-page-tabs">
        <button type="button" className={`main-page-tab ${mainPage === 'positions' ? 'active' : ''}`} onClick={() => { setMainPage('positions'); window.history.replaceState(null, '', '/'); }}>持仓查询</button>
        <button type="button" className={`main-page-tab ${mainPage === 'primaryArbitrage' ? 'active' : ''}`} onClick={() => { setMainPage('primaryArbitrage'); window.history.replaceState(null, '', '/primary-arbitrage'); }}>一级预测套利</button>
        <button type="button" className={`main-page-tab ${mainPage === 'volumeSpike' ? 'active' : ''}`} onClick={() => { setMainPage('volumeSpike'); window.history.replaceState(null, '', '/volume-spike'); }}>二级量价异动</button>
        <button type="button" className={`main-page-tab ${mainPage === 'wanwuMeme' ? 'active' : ''}`} onClick={() => { setMainPage('wanwuMeme'); window.history.replaceState(null, '', '/wanwu-meme'); }}>万物MEME</button>
        <button type="button" className={`main-page-tab ${mainPage === 'polyOptions' ? 'active' : ''}`} onClick={() => { setMainPage('polyOptions'); window.history.replaceState(null, '', '/poly-options'); }}>加密期权</button>
        <button type="button" className={`main-page-tab ${mainPage === 'lpArb' ? 'active' : ''}`} onClick={() => { setMainPage('lpArb'); window.history.replaceState(null, '', '/lp-arb'); }}>LP流动套利</button>
        <button type="button" className={`main-page-tab ${mainPage === 'globalInfo' ? 'active' : ''}`} onClick={() => { setMainPage('globalInfo'); window.history.replaceState(null, '', '/global-info'); }}>全球信息</button>
        <button type="button" className={`main-page-tab ${mainPage === 'convertibleBond' ? 'active' : ''}`} onClick={() => { setMainPage('convertibleBond'); window.history.replaceState(null, '', '/convertible-bond'); }}>可转债申购</button>
        <button type="button" className={`main-page-tab ${mainPage === 'tapeoutNand' ? 'active' : ''}`} onClick={() => { setMainPage('tapeoutNand'); window.history.replaceState(null, '', '/bem-mining'); }}>BEM挖矿</button>
        <button type="button" className={`main-page-tab ${mainPage === 'marsMining' ? 'active' : ''}`} onClick={() => { setMainPage('marsMining'); window.history.replaceState(null, '', '/mars-mining'); }}>火星挖矿</button>
      </div>
      {mainPage === 'primaryArbitrage' ? (
        <PrimaryArbitragePage />
      ) : mainPage === 'volumeSpike' ? (
        <VolumeSpikePage />
      ) : mainPage === 'wanwuMeme' ? (
        <WanwuMemePage />
      ) : mainPage === 'tapeoutNand' ? (
        <TapeOutNandPage />
      ) : mainPage === 'marsMining' ? (
        <MarsMiningPage />
      ) : mainPage === 'polyOptions' ? (
        <PolyOptionsPage />
      ) : mainPage === 'lpArb' ? (
        <LpArbPage />
      ) : mainPage === 'globalInfo' ? (
        <GlobalInfoPage />
      ) : mainPage === 'convertibleBond' ? (
        <ConvertibleBondPage />
      ) : (
        <>
      <header className="positions-header">
        <h1>Predict 我的持倉</h1>
        <p className="positions-subtitle">僅支援存款帳戶查詢，不需私鑰</p>
      </header>

      <section className="positions-query-card">
        <h2 className="positions-section-title">帳戶查詢</h2>
        <p className="positions-query-desc">
          填寫 Predict API Key 與存款帳戶地址（Smart Wallet），查詢持倉與掛單。
        </p>
        <div className="positions-form">
          <div className="positions-form-row">
            <label className="positions-label">API Key</label>
            <input
              type="password"
              className="positions-input"
              placeholder="請輸入 Predict API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="positions-form-row">
            <label className="positions-label">存款帳戶地址</label>
            <input
              type="text"
              className="positions-input positions-input-address"
              placeholder="0x..."
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="positions-form-row positions-form-actions">
            <label className="positions-checkbox-label">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              記住 API Key 與帳戶（同步後端，刷新後保留）
            </label>
            <button
              type="button"
              className="positions-query-btn"
              onClick={() => loadQuery()}
              disabled={loading || !apiKey.trim() || !address.trim()}
            >
              {loading ? '查詢中…' : '查詢持倉'}
            </button>
          </div>
          {hasQueried && (
            <>
              <div className="positions-form-row positions-poll-row">
                <label className="positions-label">輪詢間隔（持倉／掛單自動更新）</label>
                <select
                  className="positions-poll-select"
                  value={pollIntervalSec}
                  onChange={(e) => setPollIntervalSec(Number(e.target.value))}
                >
                  {POLL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="positions-form-row positions-poll-row">
                <label className="positions-label">Poly 市場輪詢間隔（買一／賣一價格，至少 1 秒）</label>
                <select
                  className="positions-poll-select"
                  value={polyPollIntervalSec}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPolyPollIntervalSec(v);
                    try { localStorage.setItem(STORAGE_POLY_POLL, String(v)); } catch (_) {}
                  }}
                >
                  {POLY_POLL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </section>

      {savedAccounts.length > 0 && (
        <section className="positions-query-card positions-current-account-bar">
          <h2 className="positions-section-title">當前查看帳戶</h2>
          <p className="positions-query-desc">點擊切換帳戶，若已查詢過會自動刷新該帳戶持倉與掛單。</p>
          <div className="account-tabs-row">
            {savedAccounts.map((acc) => {
              const isActive = (acc.address || '').toLowerCase() === (address || '').toLowerCase();
              const label = acc.note?.trim() || `${(acc.address || '').slice(0, 6)}…${(acc.address || '').slice(-4)}`;
              return (
                <button
                  key={acc.id}
                  type="button"
                  className={`account-tab-btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleSelectAccount(acc)}
                  title={acc.address}
                >
                  {acc.privateKey && <span className="account-tab-key-icon" title="已設私鑰">🔑</span>}
                  <span className="account-tab-label">{label}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="positions-query-card">
        <h2 className="positions-section-title">機器人配置</h2>
        <p className="positions-query-desc">
          配置訂單提醒推送。填寫 Webhook 後點擊「同步到後端」保存。
        </p>
        <div className="positions-form">
          <div className="positions-form-row">
            <label className="positions-label">企业微信 Webhook URL</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <input
                type="text"
                className="positions-input"
                placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=xxxxx"
                value={wecomWebhookUrl}
                onChange={(e) => setWecomWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <span className="positions-query-desc">
                企业微信群组 → 新增群机器人 → 复制 webhook 地址
              </span>
            </div>
          </div>
          <div className="positions-form-row">
            <label className="positions-label">钉钉 Webhook URL</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <input
                type="text"
                className="positions-input"
                placeholder="https://oapi.dingtalk.com/robot/send?access_token=xxxxx"
                value={dingtalkWebhookUrl}
                onChange={(e) => setDingtalkWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <span className="positions-query-desc">
                钉钉群 → 智能群助手 → 自定义机器人 → 复制 webhook 地址
              </span>
            </div>
          </div>
          <div className="positions-form-row">
            <span className="positions-label">提醒類型</span>
            <div className="positions-telegram-alerts" style={{ flexWrap: 'wrap', gap: '8px 16px' }}>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderPlaced}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderPlaced: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                掛單提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderCancelled}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderCancelled: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                撤單提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderFilled}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderFilled: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                成交提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.orderDanger}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, orderDanger: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                危險狀態提醒
              </label>
              <label className="positions-checkbox-label">
                <input
                  type="checkbox"
                  checked={!!telegramAlerts.notAtBest}
                  onChange={(e) => {
                    const next = { ...telegramAlerts, notAtBest: e.target.checked };
                    setTelegramAlerts(next);
                    syncConfig({ telegramAlerts: next });
                  }}
                />
                買一賣一提醒
              </label>
            </div>
          </div>
          <div className="positions-form-row">
            <label className="positions-label">輪詢間隔（秒）</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                className="positions-input"
                style={{ maxWidth: 120 }}
                min={10}
                max={3600}
                value={telegramPollSec}
                onChange={(e) => {
                  const v = Number(e.target.value) || 10;
                  const clamped = Math.max(10, Math.min(3600, v));
                  setTelegramPollSec(clamped);
                  syncConfig({ telegramPollIntervalSec: clamped });
                }}
              />
              <span className="positions-query-desc">10～3600，越小越即時</span>
            </div>
          </div>
          {API_BASE && (
            <div className="positions-form-row">
              <label className="positions-label">10 秒輪詢觸發 URL</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <code style={{ fontSize: 12, wordBreak: 'break-all', background: 'rgba(30,41,59,0.8)', padding: 8, borderRadius: 8 }}>
                  {api('/api/cron/order-check')}
                </code>
                <span className="positions-query-desc">
                  雲端 Cron 最小 1 分鐘。若要 10 秒輪詢，請用外部定時服務（如 cron-job.org、自建腳本）每 10 秒 GET 此 URL。若設了 wrangler secret CRON_TRIGGER_SECRET，需帶 X-Cron-Secret 或 ?secret=xxx
                </span>
              </div>
            </div>
          )}
          <div className="positions-form-row positions-form-actions" style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="positions-query-btn"
              onClick={() => syncConfig({
                wecomWebhookUrl: wecomWebhookUrl.trim(),
                dingtalkWebhookUrl: dingtalkWebhookUrl.trim(),
                telegramAlerts,
                telegramPollIntervalSec: telegramPollSec,
              })}
            >
              同步到後端
            </button>
            <TestBotButton />
          </div>
        </div>
      </section>

      {error && (
        <div className="positions-error">
          {error}
          <button type="button" className="positions-retry-btn" onClick={() => loadQuery()}>
            重試
          </button>
        </div>
      )}

      {(account || configLoaded) && (
        <section className="positions-account-card">
          <h2 className="positions-section-title">帳戶資訊</h2>
          {account && hasQueried && !loading && (
            <div className="positions-account-row">
              <div className="positions-account-info">
                <div className="positions-account-name">
                  存款帳戶
                  {currentAccountNote && (
                    <span className="positions-account-note-badge">{currentAccountNote}</span>
                  )}
                </div>
                <div className="positions-account-address" title={account.address}>
                  {account.address
                    ? `${account.address.slice(0, 6)}…${account.address.slice(-4)}`
                    : '—'}
                </div>
              </div>
            </div>
          )}
          <AccountManager
            accounts={savedAccounts}
            currentAddress={address}
            onSelect={handleSelectAccount}
            onAdd={handleAddAccount}
            onUpdate={handleUpdateAccount}
            onDelete={handleDeleteAccount}
          />
        </section>
      )}

      {hasQueried && (
        <section className={`positions-list-section ${loading ? 'positions-list-loading' : ''}`}>
          {loading && (
            <div className="positions-loading-overlay">
              <span>載入中…</span>
            </div>
          )}
          <div className="positions-list-current-account">
            當前帳戶：<strong>{currentAccountNote || (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '—')}</strong>
          </div>
          <div className="positions-tabs">
            <button
              type="button"
              className={`positions-tab ${activeTab === 'positions' ? 'active' : ''}`}
              onClick={() => setActiveTab('positions')}
            >
              Positions ({positions.length})
            </button>
            <button
              type="button"
              className={`positions-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Open Orders ({orders.length})
            </button>
            <button
              type="button"
              className={`positions-tab ${activeTab === 'marketMaking' ? 'active' : ''}`}
              onClick={() => setActiveTab('marketMaking')}
            >
              做市 ({orders.filter((o) => o.id && marketMakingByMarketId[String(o.marketId)]).length})
            </button>
          </div>

          {activeTab === 'positions' && (
            <>
              <h2 className="positions-section-title">
                持倉列表 {positions.length > 0 && `(${positions.length})`}
              </h2>
              {positions.length === 0 && !error ? (
            <div className="positions-empty">
              目前無持倉，或請確認 API Key、地址是否正確
            </div>
          ) : (
            <>
              <div className="positions-cards-header">
                <span className="positions-ch-outcome">Outcome</span>
                <span className="positions-ch-shares">Shares</span>
                <span className="positions-ch-value">Value</span>
                <span className="positions-ch-towin">To Win</span>
                <span className="positions-ch-action" />
              </div>
              <ul className="positions-cards-list">
                {positions.map((pos) => {
                  const market = pos.market || {};
                  const outcome = pos.outcome || {};
                  const question =
                    market.question || market.title || `市場 ${market.id ?? '—'}`;
                  const outcomeName =
                    outcome.name ??
                    (outcome.indexSet === 1
                      ? 'Yes'
                      : outcome.indexSet === 2
                        ? 'No'
                        : '—');
                  const isYes = outcomeName.toLowerCase() === 'yes';
                  const amountNum =
                    pos.amount != null ? Number(pos.amount) : null;
                  const valueUsd =
                    pos.valueUsd != null ? Number(pos.valueUsd) : null;
                  const shares =
                    amountNum != null && !Number.isNaN(amountNum)
                      ? amountNum >= 1e10
                        ? (amountNum / 1e18).toFixed(2)
                        : amountNum.toFixed(2)
                      : '—';
                  const valueStr =
                    valueUsd != null && !Number.isNaN(valueUsd)
                      ? `$${valueUsd.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '—';
                  const toWinNum =
                    amountNum != null && !Number.isNaN(amountNum)
                      ? amountNum >= 1e10
                        ? amountNum / 1e18
                        : amountNum
                      : null;
                  const toWinStr =
                    toWinNum != null
                      ? `$${Number(toWinNum).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '—';
                  const marketSlug = market.categorySlug || market.slug || market.id;
                  const sellLink =
                    market.id != null
                      ? `https://predict.fun/market/${marketSlug || market.id}`
                      : 'https://predict.fun';
                  return (
                    <li
                      key={
                        pos.id ||
                        `${market.id}-${outcome.onChainId || outcome.indexSet}`
                      }
                      className="position-card"
                    >
                      <div className="position-card-market">
                        <div className="position-card-icon">
                          {market.imageUrl ? (
                            <img
                              src={market.imageUrl}
                              alt=""
                              className="position-card-icon-img"
                            />
                          ) : (
                            <span className="position-card-icon-placeholder">
                              {question.slice(0, 1).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <h3 className="position-card-question" title={question}>
                          {question}
                        </h3>
                      </div>
                      <div className="position-card-details">
                        <span
                          className={`position-outcome ${isYes ? 'outcome-yes' : 'outcome-no'}`}
                        >
                          {outcomeName}
                        </span>
                        <span className="position-shares">{shares}</span>
                        <span className="position-value">{valueStr}</span>
                        <span className="position-towin">{toWinStr}</span>
                        <a
                          href={sellLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="position-sell-btn"
                        >
                          Sell ({valueStr})
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {cursor && (
            <div className="positions-load-more">
              <button
                type="button"
                className="positions-load-more-btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? '載入中…' : '載入更多'}
              </button>
            </div>
          )}
            </>
          )}

          <div
            className="positions-orders-tab-panel"
            style={{ display: activeTab === 'orders' ? 'block' : 'none' }}
          >
            <>
              <h2 className="positions-section-title">
                掛單列表 {orders.length > 0 && `(${orders.length})`}
              </h2>
              <div className="positions-form-row" style={{ marginBottom: 12 }}>
                <span className="positions-label">Polymarket 風險自動撤單</span>
                <div className="positions-telegram-alerts">
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.danger}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, danger: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當狀態為「危險」時，立即撤掉該市場所有掛單
                  </label>
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.balancedLowBook}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, balancedLowBook: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當狀態為「均衡」且 Poly 買一 shares &lt; 門檻時自動撤單
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="positions-label">門檻（shares）</span>
                    <input
                      type="number"
                      className="positions-input"
                      style={{ maxWidth: 140 }}
                      min={100}
                      max={1000000}
                      value={polyAutoCancel.balancedLowBookMinShares}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        const clamped = Math.max(100, Math.min(1000000, v));
                        const next = { ...polyAutoCancel, balancedLowBookMinShares: clamped };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                  </div>
                  <label className="positions-checkbox-label">
                    <input
                      type="checkbox"
                      checked={!!polyAutoCancel.cancelOnPartialFill}
                      onChange={(e) => {
                        const next = { ...polyAutoCancel, cancelOnPartialFill: e.target.checked };
                        setPolyAutoCancel(next);
                        if (remember) syncConfig({ polyAutoCancel: next });
                      }}
                    />
                    當訂單有部分成交時，立即撤掉該筆剩餘掛單（例：掛 1000 成交 1 → 撤掉剩餘 999）；賣單永不觸發
                  </label>
                  {!!polyAutoCancel.cancelOnPartialFill && (
                    <div style={{ marginLeft: 8, marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label className="positions-checkbox-label" style={{ alignItems: 'flex-start' }}>
                        <input
                          type="radio"
                          name="partialFillMode"
                          checked={polyAutoCancel.partialFillAllBuys !== false}
                          onChange={() => {
                            const next = { ...polyAutoCancel, partialFillAllBuys: true };
                            setPolyAutoCancel(next);
                            if (remember) syncConfig({ polyAutoCancel: next });
                          }}
                        />
                        <span>对所有买单生效（自选关闭时）</span>
                      </label>
                      <label className="positions-checkbox-label" style={{ alignItems: 'flex-start' }}>
                        <input
                          type="radio"
                          name="partialFillMode"
                          checked={polyAutoCancel.partialFillAllBuys === false}
                          onChange={() => {
                            const next = { ...polyAutoCancel, partialFillAllBuys: false };
                            setPolyAutoCancel(next);
                            if (remember) syncConfig({ polyAutoCancel: next });
                          }}
                        />
                        <span>仅在挂单列表「部分成交撤单」列勾选的买单生效</span>
                      </label>
                      <span className="positions-query-desc" style={{ display: 'block' }}>
                        自选模式下若未勾选任何买单，则不会对任何订单执行部分成交撤单。
                      </span>
                    </div>
                  )}
                  {isPredict2Site && (
                  <div className="positions-telegram-item" style={{ marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span>價差閾值</span>
                      <input
                        type="number"
                        className="positions-input"
                        style={{ width: 72 }}
                        min={0}
                        max={50}
                        value={polyAutoCancel.priceSpreadThresholdCents ?? 0}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(50, Math.floor(Number(e.target.value) || 0)));
                          const next = { ...polyAutoCancel, priceSpreadThresholdCents: v };
                          setPolyAutoCancel(next);
                          if (remember) syncConfig({ polyAutoCancel: next });
                        }}
                      />
                      <span>¢</span>
                    </label>
                    <span className="positions-query-desc" style={{ display: 'block', marginTop: 4 }}>價差 ≤ 此值不撤單，&gt; 才撤單；0＝嚴格</span>
                  </div>
                  )}
                  </div>
                </div>
              {ordersError && (
                <div className="positions-orders-error">
                  {ordersError}
                  <span className="orders-error-hint">
                    （若為 Cloudflare 部署：查挂单與一鍵撤單並掛到買一皆需 JWT，請在「已保存帳戶」填寫該帳戶的 Privy 私鑰即可，無需本地後端；
                    若為本地後端且顯示「需要 JWT」，請在「已保存帳戶」填寫該帳戶的 Privy 私鑰；
                    若顯示「目前無有效買一／賣一價格」，代表該市場暫時沒有報價，只能先撤單，稍後再試自動掛單）
                  </span>
                </div>
              )}
              {orders.length === 0 && !ordersError ? (
                <div className="positions-empty">
                  目前無掛單，或請確認 API 是否支援取得掛單
                </div>
              ) : (
                <>
                  <OpenOrdersList
                    orders={orders}
                    markets={ordersMarkets}
                    polymarketByQuestion={polymarketByQuestion}
                    polymarketLoading={polymarketLoading}
                    polymarketLinks={polymarketLinks}
                    polyOrderbookByMarket={effectivePolyOrderbook}
                    marketStartTimes={marketStartTimes}
                    cancelBeforeStartByMarket={cancelBeforeStartByMarket}
                    polyOutcomes={polyOutcomes}
                    polyOutcomeOverrides={polyOutcomeOverrides}
                    onSetPolyOverride={(marketId, opt) => {
                      setPolyOutcomeOverrides((prev) => {
                        const next = { ...prev };
                        if (opt) next[marketId] = opt; else delete next[marketId];
                        return next;
                      });
                      // 點擊「強制顯示」時，同步保存到後端，刷新或換設備也能記住
                      const value = typeof opt === 'string' ? opt.trim() : '';
                      savePolyOutcome(marketId, value);
                    }}
                    polyOptionsFromApi={polyOptionsFromApi}
                    polyViewMode={polyViewMode}
                    onSetPolyViewMode={(marketId, mode) => setPolyViewMode((prev) => {
                      const next = { ...prev };
                      if (mode) next[marketId] = mode; else delete next[marketId];
                      return next;
                    })}
                    onSavePolyLink={savePolymarketLink}
                    onDeletePolyLink={deletePolymarketLink}
                    onSaveStartTime={saveStartTime}
                    onToggleCancelBeforeStart={toggleCancelBeforeStart}
                    onSavePolyOutcome={savePolyOutcome}
                    onReplaceAtBest={replaceAtBest}
                    replaceOrderId={replaceOrderId}
                    onRemoveOrder={removeOrder}
                    removingOrderId={removingOrderId}
                    partialFillAllBuys={polyAutoCancel.partialFillAllBuys !== false}
                    partialFillOrderIds={polyAutoCancel.partialFillOrderIds}
                    cancelOnPartialFillEnabled={!!polyAutoCancel.cancelOnPartialFill}
                    skipNotAtBestReminderOrderIds={polyAutoCancel.skipNotAtBestReminderOrderIds}
                    telegramNotAtBest={telegramAlerts.notAtBest !== false}
                    onTogglePartialFillOrderId={togglePartialFillOrderId}
                    onToggleSkipNotAtBestReminderId={toggleSkipNotAtBestReminderId}
                    polyAutoOptions={{
                      autoCancelDanger: !!polyAutoCancel.danger,
                      autoCancelBalancedLowBook: !!polyAutoCancel.balancedLowBook,
                      balancedLowBookMinShares: polyAutoCancel.balancedLowBookMinShares,
                      autoCancelOnPartialFill: !!polyAutoCancel.cancelOnPartialFill,
                      priceSpreadThresholdCents: Math.max(0, Math.min(50, Math.floor(Number(polyAutoCancel.priceSpreadThresholdCents) || 0))),
                    }}
                    autoRelist5MinByMarketId={autoRelist5MinByMarketId}
                    onToggleAutoRelist5MinMarket={toggleAutoRelist5MinMarket}
                    marketMakingByMarketId={marketMakingByMarketId}
                    onToggleMarketMakingMarket={toggleMarketMakingMarket}
                  />
                  {polymarketError && (
                    <div className="polymarket-inline-error">{polymarketError}</div>
                  )}
                </>
              )}
            </>
          </div>

          <div
            className="positions-orders-tab-panel"
            style={{ display: activeTab === 'marketMaking' ? 'block' : 'none' }}
          >
            <h2 className="positions-section-title">做市</h2>
            <p className="positions-query-desc" style={{ marginBottom: 12 }}>
              在掛單列表中，於各市場標題旁勾選「做市」後，該市場全部訂單會出現在此。此處僅提供：撤單、掛單到買一／賣一、填寫新價格（¢）後加價重掛（股數不變）。若某市場 outcome 下僅為做市訂單，將不請求 Polymarket 輪詢。
            </p>
            {ordersError && (
              <div className="positions-orders-error" style={{ marginBottom: 12 }}>{ordersError}</div>
            )}
            {orders.length === 0 ? (
              <div className="positions-empty">暫無掛單</div>
            ) : (
              (() => {
                const mmOrders = orders.filter((o) => o.id && marketMakingByMarketId[String(o.marketId)]);
                if (mmOrders.length === 0) {
                  return <div className="positions-empty">請先在各市場標題旁勾選「做市」</div>;
                }
                return (
                  <div className="orders-table-wrap">
                    <table className="orders-table">
                      <thead>
                        <tr>
                          <th>市場</th>
                          <th>Side</th>
                          <th>Outcome</th>
                          <th>價格</th>
                          <th>股數</th>
                          <th>新價(¢)</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mmOrders.map((item) => {
                          const order = item.order || {};
                          const m = ordersMarkets[item.marketId] || {};
                          const q = m.question || m.title || String(item.marketId);
                          const isBuy = order.side === 0;
                          const side = isBuy ? 'Buy' : 'Sell';
                          const tokenId = order.tokenId;
                          const outcomeName = getOutcomeName(tokenId, m.outcomes);
                          const isYes = String(outcomeName).toLowerCase() === 'yes';
                          const makerAmt = Number(order.makerAmount) || 0;
                          const takerAmt = Number(order.takerAmount) || 0;
                          const priceDecimal =
                            isBuy
                              ? takerAmt > 0 ? makerAmt / takerAmt : null
                              : makerAmt > 0 ? takerAmt / makerAmt : null;
                          const priceStr =
                            priceDecimal != null && Number.isFinite(priceDecimal)
                              ? `${(priceDecimal * 100).toFixed(1)}¢`
                              : '—';
                          const amountNum = Number(item.amount) || 0;
                          const amountHuman = amountNum >= 1e10 ? amountNum / 1e18 : amountNum;
                          const sharesStr =
                            amountNum > 0
                              ? amountHuman >= 1
                                ? amountHuman.toFixed(2)
                                : amountHuman.toString()
                              : '—';
                          const oid = String(item.id);
                          return (
                            <tr key={oid}>
                              <td className="orders-cell-label" title={q}>{q.length > 42 ? `${q.slice(0, 42)}…` : q}</td>
                              <td>{side}</td>
                              <td className={isYes ? 'outcome-yes' : 'outcome-no'}>{outcomeName || '—'}</td>
                              <td>{priceStr}</td>
                              <td>{sharesStr}</td>
                              <td>
                                <input
                                  type="number"
                                  className="positions-input"
                                  style={{ width: 64 }}
                                  min={1}
                                  max={99}
                                  step={1}
                                  placeholder="¢"
                                  value={mmManualPriceCents[oid] ?? ''}
                                  onChange={(e) =>
                                    setMmManualPriceCents((prev) => ({ ...prev, [oid]: e.target.value }))
                                  }
                                />
                              </td>
                              <td className="orders-cell-action">
                                <button
                                  type="button"
                                  className="orders-remove-btn"
                                  disabled={removingOrderId === item.id}
                                  onClick={() => removeOrder(item)}
                                >
                                  {removingOrderId === item.id ? '…' : '撤单'}
                                </button>
                                <button
                                  type="button"
                                  className="orders-replace-btn"
                                  disabled={replaceOrderId === item.id}
                                  onClick={() =>
                                    replaceAtBest({
                                      orderId: item.id,
                                      marketId: item.marketId,
                                      tokenId: order.tokenId,
                                      side: order.side,
                                      sharesWei: item.amount,
                                    })
                                  }
                                >
                                  {replaceOrderId === item.id ? '…' : (isBuy ? '挂单到买一' : '挂单到卖一')}
                                </button>
                                <button
                                  type="button"
                                  className="orders-replace-btn"
                                  disabled={replaceOrderId === item.id}
                                  onClick={() => replaceAtManualCents(item, mmManualPriceCents[oid])}
                                >
                                  加价格挂单
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>

        </section>
      )}
        </>
      )}
    </div>
  );
}
